// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: 29-app-environment-variables.md
// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md
// @greenlight-spec: decisions/0027-integration-grant-approval-policy-placement.md
// @greenlight-spec: decisions/0039-injected-grant-is-disclosure.md
import * as React from 'react';
import { AlertTriangle, ChevronDown, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { IntegrationTile } from '@/components/greenlight/IntegrationTile';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { Walkthrough } from '@/components/greenlight/Walkthrough';
import { Stepper, type StepConfig } from '@/components/install/Stepper';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  AUTH_MODE_DISPLAY_NAMES,
  CONNECTED_DB_ENGINES,
  RESERVED_ENV_NAME_SET,
  applyUrlPlaceholders,
  catalogDisplayNamePlaceholder,
  catalogSlugPlaceholder,
  extractPlaceholderTokens,
  injectedEnvNamesFor,
  isInjectableConnectedDbAuthMode,
  isPerUserOauthIntegration,
  isSalesforceMyDomain,
  isSecretlessAuthMode,
  normalizeCredentialRole,
  normalizePlaceholderValue,
  renderSetupStatement,
  setupSecurityIntegrationName,
  CONNECTED_DB_ENV_PREFIX_MAX_LENGTH,
} from '@greenlight/shared';
import type {
  AuthConfig,
  CatalogConnectionProfileDto,
  CatalogEntryDto,
  CatalogUrlPlaceholderDto,
  IntegrationAuthMode,
  IntegrationDeliveryMode,
  IntegrationListItem,
  MicrosoftFabricUserPiece,
} from '@greenlight/shared';

import { AcquisitionGuidePanel } from './AcquisitionGuidePanel';
import { CatalogPicker } from './CatalogPicker';
import { CredentialFieldset, EMPTY_CREDENTIAL, type CredentialDraft } from './CredentialFieldset';
import {
  CredentialSecretInputs,
  EMPTY_SECRET_DRAFT,
  clientIdLabelFor,
  clientIdPlaceholderFor,
  composeSecretDraft,
  isSecretDraftEmpty,
  secretDraftError,
  secretNounFor,
  secretShapeFor,
  type SecretDraft,
} from './CredentialSecretInputs';
import { AuthConfigFieldInputs, CredentialPlacementFields } from './CredentialPlacementFields';
import { DataAccessTermsPanel } from './DataAccessTermsPanel';
import { FabricRegistrationForm } from './FabricRegistrationForm';
import { SignInTestPanel, type SignInTestPanelTarget } from './SignInTest';
import { fabricPieceFor } from './fabricRegistration';
import { DeliveryModeSelector } from './DeliveryModeSelector';
import { BaseUrlField, DatabaseNameField, EnvVarNameField } from './IntegrationFields';
import {
  authConfigFieldErrors,
  authConfigFieldErrorsFor,
  actsAsSignedInUserFor,
  authConfigFieldsFor,
  buildAuthConfig,
  buildCustomServiceAccountConfig,
  composeClientCredentialsSecret,
  composeGithubAppSecret,
  composePrivateKeyJwtSecret,
  defaultCredentialSlugFor,
  defaultSelectableProfile,
  editUrlShapeFor,
  machineNameFor,
  hasUnresolvedPlaceholder,
  isGithubApp,
  isPrivateKeyJwt,
  isValidHeaderName,
  isValidQueryParamName,
  nonSecretAuthParamNames,
  collectMintScopesFor,
  parseScopes,
  prefillFromCatalog,
  suggestedCatalogSlug,
  selectableProfiles,
  urlPlaceholdersFor,
  type BasicKeyPosition,
  type CustomAuthInjectKind,
  extractApiError,
} from './catalogRegistration';
import { isPlainHttpUrl } from './IntegrationFields';
import {
  CertificateTrustSection,
  tlsConfigFrom,
  tlsFormError,
  tlsFormStateFrom,
  VERIFY_TLS,
  type TlsFormState,
} from './CertificateTrustSection';
import {
  reachabilityKey,
  RegisterConnectivityStep,
  type ReachabilityReport,
  type ReachabilityTargets,
} from './RegisterConnectivityStep';
import { UrlPlaceholderFields } from './UrlPlaceholderFields';
import { integrationWalkthroughFor } from '@/walkthroughs/integrations';

function isUrlWithProtocol(value: string, protocols: readonly string[]): boolean {
  try {
    const url = new URL(value);
    return protocols.includes(url.protocol);
  } catch {
    return false;
  }
}

const isHttpUrl = (value: string): boolean => isUrlWithProtocol(value, ['http:', 'https:']);
const isHttpsUrl = (value: string): boolean => isUrlWithProtocol(value, ['https:']);

const ENV_VAR_NAME_RE = /^[A-Z][A-Z0-9_]{0,127}$/;

// A database's env var name is a prefix, so its derived per-field names are
// what must clear the reserved set.
function envVarNameError(
  name: string,
  isDatabase: boolean,
  taken: ReadonlySet<string>,
  original?: string,
): string | undefined {
  if (!name) return 'Required for injected delivery';
  if (!ENV_VAR_NAME_RE.test(name)) return 'Use A–Z, 0–9, _ (must start with a letter)';
  if (isDatabase && name.length > CONNECTED_DB_ENV_PREFIX_MAX_LENGTH) {
    return `Use at most ${CONNECTED_DB_ENV_PREFIX_MAX_LENGTH} characters`;
  }
  const reserved = injectedEnvNamesFor(name, isDatabase).find((n) => RESERVED_ENV_NAME_SET.has(n));
  if (reserved) return `${reserved} is a reserved managed name`;
  if (name !== original && taken.has(name)) return `${name} is already used by another integration`;
  return undefined;
}
const CREDENTIAL_SLUG_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
// Entra client/tenant IDs are UUIDs; the contract rejects anything else, so
// catch the paste mistake (app name, secret ID, domain) before submit.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface RegisterForm {
  displayName: string;
  name: string;
  baseUrl: string;
  authMode: string;
  host: string;
  port: string;
  database: string;
  username: string;
  clientId: string;
  credentialRole: string;
  certificateId: string;
  tenantId: string;
  deliveryMode: IntegrationDeliveryMode;
  envVarName: string;
  tokenUrl: string;
  scopes: string;
  authorizeUrl: string;
  revokeUrl: string;
  authInjectKind: CustomAuthInjectKind;
  basicKeyPosition: BasicKeyPosition;
  authHeaderName: string;
  authQueryParamName: string;
  authUsername: string;
  authConfigParams: Record<string, string>;
  placeholderValues: Record<string, string>;
  knowledgeBodyMd: string;
}

const EMPTY_FORM: RegisterForm = {
  displayName: '',
  name: '',
  baseUrl: '',
  authMode: '',
  host: '',
  port: '',
  database: '',
  username: '',
  clientId: '',
  credentialRole: '',
  certificateId: '',
  tenantId: '',
  deliveryMode: 'proxied',
  envVarName: '',
  tokenUrl: '',
  scopes: '',
  authorizeUrl: '',
  revokeUrl: '',
  authInjectKind: 'bearer',
  basicKeyPosition: 'username',
  authHeaderName: '',
  authQueryParamName: '',
  authUsername: '',
  authConfigParams: {},
  placeholderValues: {},
  knowledgeBodyMd: '',
};

/** The editable-in-edit-mode subset of the form, seeded from an existing integration. */
function editFormFrom(integration: IntegrationListItem): RegisterForm {
  const urlShape = editUrlShapeFor(integration);
  return {
    ...EMPTY_FORM,
    displayName: integration.name,
    name: integration.slug,
    authMode: integration.auth_mode,
    baseUrl: integration.base_url ?? '',
    host: integration.host ?? '',
    port: integration.port != null ? String(integration.port) : '',
    database: integration.database_name ?? '',
    deliveryMode: integration.delivery_mode,
    envVarName: integration.env_var_name ?? '',
    // Seeded so an edit round-trips: the field renders what is stored, and a
    // submit sends the whole object back (auth_config is replaced, not merged).
    scopes: scopesFrom(integration.auth_config),
    authConfigParams: authConfigParamsFrom(integration),
    tokenUrl: stringAt(integration.auth_config, 'token_url'),
    authorizeUrl: stringAt(integration.auth_config, 'authorize_url'),
    placeholderValues: urlShape.kind === 'placeholders' ? urlShape.templates.values : {},
  };
}

function stringAt(config: Record<string, unknown> | null | undefined, key: string): string {
  const value = config?.[key];
  return typeof value === 'string' ? value : '';
}

/** The stored values of the profile's customer-specific auth_config fields. */
function authConfigParamsFrom(integration: IntegrationListItem): Record<string, string> {
  const fields = authConfigFieldsFor(integration.catalog_key, integration.auth_mode);
  return Object.fromEntries(
    fields.map((field) => {
      const value = integration.auth_config?.[field.key];
      return [field.key, typeof value === 'string' ? value : ''];
    }),
  );
}

/** `auth_config.scopes` as the space-separated string the field edits. */
function scopesFrom(authConfig: Record<string, unknown> | null | undefined): string {
  const scopes = authConfig?.['scopes'];
  return Array.isArray(scopes) ? scopes.filter((v) => typeof v === 'string').join(' ') : '';
}

interface FormErrors {
  authorizeUrl?: string;
  revokeUrl?: string;
  displayName?: string;
  name?: string;
  baseUrl?: string;
  authMode?: string;
  host?: string;
  port?: string;
  database?: string;
  username?: string;
  clientId?: string;
  credentialRole?: string;
  certificateId?: string;
  tenantId?: string;
  envVarName?: string;
  tokenUrl?: string;
  scopes?: string;
  credentials?: string;
  authPlacement?: string;
  authUsername?: string;
  authConfigParams?: Record<string, string>;
  placeholders?: Record<string, string>;
  insecureHttp?: string;
  tls?: string;
}

type WizardStep = 'pick' | 'setup' | 'name' | 'connect' | 'credential' | 'delivery' | 'review';

const WIZARD_ORDER: readonly WizardStep[] = [
  'pick',
  'setup',
  'name',
  'connect',
  'credential',
  'delivery',
  'review',
];

const STEP_LABELS: Record<WizardStep, string> = {
  pick: 'Choose',
  setup: 'Set up',
  name: 'Name',
  connect: 'Connect',
  credential: 'Credential',
  delivery: 'Delivery',
  review: 'Review',
};

/** Which step owns each validation error, so Continue checks only the step on screen. */
const STEP_ERROR_KEYS: Record<WizardStep, readonly (keyof FormErrors)[]> = {
  pick: [],
  setup: ['authMode'],
  name: ['displayName', 'name'],
  connect: [
    'baseUrl',
    'host',
    'port',
    'database',
    'tokenUrl',
    'scopes',
    'authorizeUrl',
    'revokeUrl',
    'authPlacement',
    'authUsername',
    'authConfigParams',
    'placeholders',
    'insecureHttp',
    'tls',
  ],
  credential: [
    'credentials',
    'clientId',
    'certificateId',
    'tenantId',
    'username',
    'credentialRole',
  ],
  delivery: ['envVarName'],
  review: [],
};

const SECRET_KIND_LABELS: Record<string, string> = {
  api_key: 'API key',
  bot_token: 'Bot token',
  personal_access_token: 'Personal access token',
  service_account_json: 'Service account key (JSON)',
};

const CUSTOM_AUTH_MODES = [
  {
    value: 'service_account',
    description: 'An API key or token that Greenlight attaches to every call.',
  },
  {
    value: 'client_credentials',
    description: 'Greenlight trades a client ID and secret for short-lived access tokens.',
  },
  {
    value: 'user_delegated',
    description: 'Each person signs in to the service, and calls run as them.',
  },
] as const satisfies readonly { value: IntegrationAuthMode; description: string }[];

function attachResolvedUrlError(
  next: FormErrors,
  template: string,
  message: string,
  fallback: 'baseUrl' | 'tokenUrl',
): void {
  const token = extractPlaceholderTokens([template])[0];
  if (token !== undefined) {
    next.placeholders = { ...next.placeholders, [token]: next.placeholders?.[token] ?? message };
    return;
  }
  next[fallback] = message;
}

/** One app named in an edit response's `affected_apps` — a delivery-mode/env-var-name warning, never a block. */
interface AffectedApp {
  id: string;
  name: string;
  slug: string;
}

export interface IntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Env var names already claimed by injected integrations (collision check). */
  injectedEnvVarNames?: string[];
  /**
   * The integration being edited, or null/undefined to register a new one.
   * Editing shares this dialog's fields with registration (docs/28
   * §/integrations) so both present identical widgets; `name`, `authMode`,
   * and (for a connected database) `engine` are immutable in edit mode and
   * render read-only, and the catalog picker + credential collection never
   * apply to an existing integration.
   */
  integration?: IntegrationListItem | null;
  /**
   * Catalog key to open straight onto, skipping the picker. Set when IT approves a
   * connection-request: the requester already named the system, so re-picking it
   * from a list of tens would be busywork.
   */
  initialCatalogKey?: string | null;
  /**
   * The org has not yet accepted Greenlight's data-access terms, so the
   * explanation panel carries the acceptance checkbox and this registration
   * must carry acceptance (docs/09 §Scope Model).
   */
  requireDataAccessTerms?: boolean;
  /** Called after a registration that carried the org's acceptance succeeds. */
  onDataAccessTermsAccepted?: () => void;
  /** Called after a credential's secret is replaced, so a test result kept for the old one is dropped. */
  onSecretReplaced?: (integrationId: string, slug: string) => void;
  /**
   * Called after a saved edit changes what a credential test runs against (base URL,
   * host, port, database, or auth config), which also clears every recorded outcome.
   */
  onTestTargetChanged?: (integrationId: string) => void;
}

/** PATCH fields whose change clears a credential's recorded test outcome on the server. */
const TEST_TARGET_FIELDS = ['baseUrl', 'host', 'port', 'databaseName', 'authConfig'] as const;
export function IntegrationDialog({
  open,
  onOpenChange,
  injectedEnvVarNames = [],
  integration = null,
  initialCatalogKey = null,
  requireDataAccessTerms = false,
  onDataAccessTermsAccepted,
  onSecretReplaced,
  onTestTargetChanged,
}: IntegrationDialogProps): React.JSX.Element {
  const isEdit = integration != null;
  const queryClient = useQueryClient();
  const create = api.integrations.create.useMutation();
  const updateIntegration = api.integrations.updateIntegration.useMutation();
  const replaceSecret = api.integrations.replaceSecret.useMutation();
  // The catalog is release-pinned; fetch it only while registering.
  const catalogQueryKey = ['integrations-catalog'];
  const catalogQuery = api.integrations.catalog.useQuery(
    catalogQueryKey,
    {},
    { queryKey: catalogQueryKey, enabled: open && !isEdit },
  );

  // Registration walks the wizard steps; edit renders one form ('form').
  const [step, setStep] = React.useState<WizardStep | 'form'>('pick');
  const [furthestStep, setFurthestStep] = React.useState<WizardStep>('pick');
  // The machine name follows the display name until IT edits it by hand.
  const [nameEdited, setNameEdited] = React.useState(false);
  const [entry, setEntry] = React.useState<CatalogEntryDto | null>(null);
  // Set while the Microsoft Fabric form is open: the pieces it starts with ticked.
  const [fabricPieces, setFabricPieces] = React.useState<MicrosoftFabricUserPiece[] | null>(null);
  // Set once a per-user OAuth registration succeeds: the dialog then offers Test sign-in.
  const [signInTests, setSignInTests] = React.useState<SignInTestPanelTarget[] | null>(null);
  const [profile, setProfile] = React.useState<CatalogConnectionProfileDto | null>(null);
  const [form, setForm] = React.useState<RegisterForm>(EMPTY_FORM);
  const [credential, setCredential] = React.useState<CredentialDraft>({ ...EMPTY_CREDENTIAL });
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [affectedApps, setAffectedApps] = React.useState<AffectedApp[] | null>(null);
  const [showCatalogDefaults, setShowCatalogDefaults] = React.useState(false);
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const [insecureHttpAcknowledged, setInsecureHttpAcknowledged] = React.useState(false);
  const [tls, setTls] = React.useState<TlsFormState>(VERIFY_TLS);
  const reachabilityCheck = api.integrations.checkReachability.useMutation();
  const [reachability, setReachability] = React.useState<{
    key: string;
    report: ReachabilityReport;
  } | null>(null);
  const needsTermsAcceptance = !isEdit && requireDataAccessTerms;
  // Edit mode: a write-only replacement secret per credential slug. Blank means
  // keep the stored value; the stored value is never shown or prefilled.
  const [secretDrafts, setSecretDrafts] = React.useState<Record<string, SecretDraft>>({});
  // What a submit reads, kept current so an edit made during the pre-submit check is noticed.
  const submitInputs = { profile, form, credential, secretDrafts, termsAccepted };
  const latestSubmitInputs = React.useRef(submitInputs);
  latestSubmitInputs.current = submitInputs;

  // Apps that hold a granted grant on this integration — fetched only while an
  // injected-delivery replacement is being entered, to name who keeps the old
  // value until their next deploy (same signal EditCredentialDialog gives).
  const grantsQueryEnabled =
    open &&
    integration != null &&
    integration.delivery_mode === 'injected' &&
    Object.values(secretDrafts).some((draft) => !isSecretDraftEmpty(draft));
  const grantedGrantsQuery = api.permissions.queue.useQuery(
    ['integration-granted', integration?.id ?? ''],
    { query: { integrationId: integration?.id ?? '', status: 'granted', limit: 100 } },
    {
      queryKey: ['integration-granted', integration?.id ?? ''],
      enabled: grantsQueryEnabled,
    },
  );
  const grantedGrantsPage =
    grantedGrantsQuery.data?.status === 200 ? grantedGrantsQuery.data.body : null;
  const injectedAffectedNames = grantedGrantsPage
    ? Array.from(
        new Set(grantedGrantsPage.items.map((grant) => grant.app_name ?? grant.requested_by)),
      ).sort((a, b) => a.localeCompare(b))
    : [];
  const injectedAffectedTruncated =
    grantedGrantsPage != null && grantedGrantsPage.next_cursor != null;

  const isCatalog = entry !== null;
  // A connected database is registered from its catalog entry (`engine` set —
  // ADR-0030) or, when editing, carries `engine` on the row itself.
  const isDatabase = isEdit ? integration.engine != null : isCatalog && entry.engine !== null;
  // The auth mode in effect: the existing row (edit), the chosen catalog
  // profile, or the Custom select — auth_mode is immutable once registered,
  // so edit mode never writes `form.authMode` after the initial seed.
  const authMode = isEdit
    ? integration.auth_mode
    : isCatalog && profile
      ? profile.auth_mode
      : form.authMode;
  const isClientCreds = authMode === 'client_credentials';
  // A user passthrough credential is one upstream OAuth client (docs/48 §Catalog
  // profiles and credentials): its id is collected beside the slug and rides as
  // the credential's non-secret config; its secret is the credential.
  const isUserDelegated = authMode === 'user_delegated';
  // A system integration is served by the control plane itself (ADR-0036): no
  // upstream, no base URL, always proxied.
  const isSystem = authMode === 'system';
  const isHttp = !isDatabase && !isSystem;
  // Only a static key may cross plain HTTP; token-bearing modes stay HTTPS-only
  // (docs/09 §Generic REST integration).
  const allowsPlainHttp = isHttp && authMode === 'service_account';
  const isBaseUrlScheme = (value: string): boolean =>
    isHttpsUrl(value) || (allowsPlainHttp && isPlainHttpUrl(value));
  // A database service principal may also be delivered to the app's native
  // driver (ADR-0030 §5); every other database mode is proxied.
  const isInjectableDb = isDatabase && isInjectableConnectedDbAuthMode(authMode);
  // Secretless modes have no secret to paste (workload identity — ADR-0030,
  // system — ADR-0036); the credential row is just the grant-binding handle.
  // Edit mode reads the same fact per credential via `vault_key === null`.
  const secretless = isSecretlessAuthMode(authMode);
  const isPrivateKeyJwtProfile = isPrivateKeyJwt(profile);
  // The org-owned GitHub App profile collects two GitHub ids + the downloaded
  // PEM; installation tokens are short-lived, so it is proxied-only (docs/09).
  const isGithubAppProfile = isGithubApp(profile);
  // A profile that mints per signed-in user (Box) is proxied-only: its tokens are
  // per user and an env var holds one value (docs/48 §Box). Edit has no picked
  // profile, so it reads the same fact from the row's catalog entry.
  const actsAsSignedInUser = isEdit
    ? actsAsSignedInUserFor(integration.catalog_key, integration.auth_mode)
    : profile?.acts_as_signed_in_user === true;
  // Google Workspace's service_account profile has no static wire shape — the
  // proxy mints Google tokens with the scopes captured here (docs/09 §Google
  // Workspace DWD). Edit has no picked profile (catalog query is registration-
  // only), so both modes read collectMintScopes from the seed.
  const needsMintScopes = collectMintScopesFor(
    isEdit ? integration.catalog_key : (entry?.catalog_key ?? null),
    authMode,
    isEdit ? integration.auth_config?.['mechanism'] : profile?.mechanism,
  );
  // Box's Enterprise ID: editable, since a wrong one refuses every user and
  // registering again is the only other fix. Registration renders these through
  // the picked profile instead.
  const editAuthConfigFields = isEdit
    ? authConfigFieldsFor(integration.catalog_key, integration.auth_mode)
    : [];
  const editUrlShape = React.useMemo(
    () => (integration ? editUrlShapeFor(integration) : null),
    [integration],
  );
  const editTemplates = editUrlShape?.kind === 'placeholders' ? editUrlShape.templates : null;
  const secretNoun = isClientCreds
    ? isPrivateKeyJwtProfile || isGithubAppProfile
      ? 'private key'
      : 'client secret'
    : isUserDelegated
      ? 'client secret'
      : !isDatabase
        ? 'secret'
        : authMode === 'service_principal'
          ? 'client secret'
          : 'password';
  // A catalogued static key is named for what IT pastes (API key, bot token);
  // every other mode by its secret noun.
  const secretLabel =
    isCatalog && authMode === 'service_account' && profile
      ? (SECRET_KIND_LABELS[profile.credential_kind] ?? 'Secret')
      : secretNoun.charAt(0).toUpperCase() + secretNoun.slice(1);
  const clientIdLabel = profile?.client_id_label ?? 'Client ID';
  const clientIdPlaceholder = profile?.client_id_placeholder ?? 'OAuth client id';
  const urlPlaceholders = isCatalog ? urlPlaceholdersFor(entry, profile) : [];
  const baseUrlTemplated = isCatalog && extractPlaceholderTokens([entry.base_url]).length > 0;
  const tokenUrlTemplated =
    isCatalog && extractPlaceholderTokens([profile?.token_url ?? '']).length > 0;
  const githubInstallationFromPlaceholder =
    isGithubAppProfile &&
    urlPlaceholders.some((placeholder) => placeholder.token === 'installation_id');
  const resolvedBaseUrl = applyUrlPlaceholders(
    form.baseUrl,
    form.placeholderValues,
    entry?.placeholders,
  );
  const resolvedTokenUrl = applyUrlPlaceholders(
    form.tokenUrl,
    form.placeholderValues,
    entry?.placeholders,
  );
  const resolvedAuthorizeUrl = applyUrlPlaceholders(
    form.authorizeUrl,
    form.placeholderValues,
    entry?.placeholders,
  );
  const connectCallbackUrl = `${window.location.origin}/connect/callback`;
  const setupWalkthrough = integrationWalkthroughFor(entry?.catalog_key, profile?.id);
  // A profile whose credentials are each one upstream role (Snowflake).
  const credentialRole = !isEdit && isCatalog ? (profile?.credential_role ?? null) : null;
  const setupStatement = profile?.setup_guide?.statement
    ? {
        title: profile.setup_guide.statement.title,
        text: renderSetupStatement(profile.setup_guide.statement.template, {
          redirect_uri: connectCallbackUrl,
          security_integration: setupSecurityIntegrationName(form.name),
        }),
      }
    : undefined;

  const placeholderFields = (
    <UrlPlaceholderFields
      placeholders={urlPlaceholders}
      values={form.placeholderValues}
      errors={errors.placeholders}
      baseUrlTemplate={form.baseUrl}
      tokenUrlTemplate={form.tokenUrl}
      authorizeUrlTemplate={form.authorizeUrl}
      onChange={(token, value) =>
        setForm((prev) => ({
          ...prev,
          placeholderValues: { ...prev.placeholderValues, [token]: value },
        }))
      }
    />
  );
  // Edit re-resolves every templated URL from the one value, so the base URL and
  // the token endpoint always name the same tenant.
  const editPlaceholderFields = editTemplates && (
    <UrlPlaceholderFields
      placeholders={editTemplates.placeholders}
      values={form.placeholderValues}
      errors={errors.placeholders}
      baseUrlTemplate={editTemplates.baseUrl}
      tokenUrlTemplate={editTemplates.tokenUrl}
      authorizeUrlTemplate={editTemplates.authorizeUrl}
      onChange={(token, value) =>
        setForm((prev) => {
          const values = { ...prev.placeholderValues, [token]: value };
          const resolve = (template: string, current: string) =>
            template ? applyUrlPlaceholders(template, values, editTemplates.placeholders) : current;
          return {
            ...prev,
            placeholderValues: values,
            baseUrl: resolve(editTemplates.baseUrl, prev.baseUrl),
            tokenUrl: resolve(editTemplates.tokenUrl, prev.tokenUrl),
            authorizeUrl: resolve(editTemplates.authorizeUrl, prev.authorizeUrl),
          };
        })
      }
    />
  );
  // A user passthrough credential, HTTP or a connected database alike.
  const upstreamClientIdField = (
    <div className="space-y-1.5">
      <Label htmlFor="reg-upstream-client-id">{clientIdLabel}</Label>
      <Input
        id="reg-upstream-client-id"
        value={form.clientId}
        onChange={(e) => set('clientId')(e.target.value)}
        placeholder={clientIdPlaceholder}
        className="font-mono"
        autoComplete="off"
      />
      <p className="text-[11.5px] leading-snug text-muted-foreground">
        The upstream registration this credential presents. Every app granted it reaches exactly
        that registration’s permissions, as each signed-in user.
      </p>
      <p className="text-[11.5px] leading-snug text-muted-foreground">
        Its redirect URI is this Greenlight’s connect callback:{' '}
        <MonoValue className="text-[11.5px]" wrap>
          {connectCallbackUrl}
        </MonoValue>
      </p>
      {errors.clientId && <p className="text-[11.5px] text-destructive">{errors.clientId}</p>}
    </div>
  );

  // Delivery modes on offer (ADR-0026 §5, ADR-0030): a connected database is
  // proxied unless its auth mode is a service principal; a non-proxyable catalog
  // entry only injected; Custom HTTP and editing an existing HTTP integration
  // both offer either.
  // A GitHub App mints 1-hour installation tokens the proxy holds; injecting a
  // short-lived token (or the App key) into a pod is not an option, so this
  // profile is proxied-only even though the entry itself stays injectable for PAT.
  const deliveryModes: readonly IntegrationDeliveryMode[] =
    isGithubAppProfile || actsAsSignedInUser || (isDatabase && !isInjectableDb)
      ? ['proxied']
      : isCatalog
        ? entry.delivery_modes
        : ['proxied', 'injected'];
  const deliveryMode: IntegrationDeliveryMode = deliveryModes.includes(form.deliveryMode)
    ? form.deliveryMode
    : deliveryModes[0]!;
  const showEnvVar = (isHttp || isInjectableDb) && deliveryMode === 'injected';

  // The catalog read projects by role (ADR-0026 §7). This dialog is IT-only, so it
  // narrows to the full projection and treats a trimmed one as no entries rather
  // than rendering a picker whose registration fields are all missing.
  const catalogBody = catalogQuery.data?.status === 200 ? catalogQuery.data.body : undefined;
  const catalogEntries = catalogBody?.projection === 'full' ? catalogBody.entries : [];
  const catalogError =
    catalogQuery.isError || (catalogQuery.data !== undefined && catalogQuery.data.status !== 200);

  // The seeded snapshot to diff an edit's changed fields against — recomputed
  // only when the target integration identity changes, not on every keystroke.
  const original = React.useMemo(
    () => (integration ? editFormFrom(integration) : EMPTY_FORM),
    [integration],
  );

  // Reset to blank/pick on close (write-only secrets must never linger) or on
  // a register-mode open/re-target; seed from the row on an edit-mode open or
  // re-target. Both branches fire on every `integration` identity change, not
  // just the open transition — the caller (IntegrationsView) mounts one dialog
  // for both triggers, so switching from one mode to the other never actually
  // closes it; without this, the edit→register direction left step stuck on
  // 'form' with the previous edit target's stale values still in `form`.
  React.useEffect(() => {
    setFabricPieces(null);
    setSignInTests(null);
    if (!open) {
      setStep('pick');
      setFurthestStep('pick');
      setNameEdited(false);
      setEntry(null);
      setProfile(null);
      setForm(EMPTY_FORM);
      setCredential({ ...EMPTY_CREDENTIAL });
      setSecretDrafts({});
      setErrors({});
      setAffectedApps(null);
      setShowCatalogDefaults(false);
      setTermsAccepted(false);
      setInsecureHttpAcknowledged(false);
      setTls(VERIFY_TLS);
      setReachability(null);
      return;
    }
    setTls(tlsFormStateFrom(integration?.tls_config ?? null));
    if (integration) {
      setStep('form');
      setEntry(null);
      setProfile(null);
      setForm(editFormFrom(integration));
      setSecretDrafts({});
      setErrors({});
      setAffectedApps(null);
      setShowCatalogDefaults(false);
    } else {
      setStep('pick');
      setFurthestStep('pick');
      setNameEdited(false);
      setEntry(null);
      setProfile(null);
      setForm(EMPTY_FORM);
      setCredential({ ...EMPTY_CREDENTIAL });
      setSecretDrafts({});
      setErrors({});
      setAffectedApps(null);
      setShowCatalogDefaults(false);
    }
  }, [open, integration]);

  // Jump past the picker when the dialog was opened for a specific catalog entry.
  // The ref makes this once-per-open: backing out to the picker on purpose must not
  // bounce the user straight back into the form.
  const appliedInitialKey = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!open) {
      appliedInitialKey.current = null;
      return;
    }
    if (integration || !initialCatalogKey) return;
    if (appliedInitialKey.current === initialCatalogKey) return;
    const match = catalogEntries.find((e) => e.catalog_key === initialCatalogKey);
    if (!match) return;
    appliedInitialKey.current = initialCatalogKey;
    pickEntry(match);
  }, [open, integration, initialCatalogKey, catalogEntries]);

  function pickEntry(picked: CatalogEntryDto, profileId?: string): void {
    const fabricPiece = fabricPieceFor(picked.catalog_key);
    if (fabricPiece !== null && profileId === undefined) {
      setFabricPieces(picked.catalog_key === 'microsoft-fabric' ? [] : [fabricPiece]);
      return;
    }
    setFabricPieces(null);
    const chosen =
      profileId !== undefined
        ? selectableProfiles(picked).find((p) => p.id === profileId)
        : defaultSelectableProfile(picked);
    if (!chosen) return; // Guarded by the seed (every entry has a selectable default).
    const prefill = prefillFromCatalog(picked, chosen);
    const delivery = picked.delivery_modes.includes('proxied') ? 'proxied' : 'injected';
    setEntry(picked);
    setProfile(chosen);
    setForm({ ...EMPTY_FORM, ...prefill, deliveryMode: delivery });
    // A system entry's single grant-binding credential is prefilled (ADR-0036),
    // so nothing is left to type.
    setCredential(
      chosen.auth_mode === 'system'
        ? {
            ...EMPTY_CREDENTIAL,
            slug: 'read',
            scope: 'Read-only org roster: id, email, display name, active status.',
          }
        : { ...EMPTY_CREDENTIAL },
    );
    setShowCatalogDefaults(false);
    setNameEdited(false);
    setErrors({});
    setStep('setup');
    setFurthestStep('setup');
  }

  function pickCustom(): void {
    setEntry(null);
    setProfile(null);
    setForm(EMPTY_FORM);
    setCredential({ ...EMPTY_CREDENTIAL });
    setNameEdited(false);
    setErrors({});
    setStep('setup');
    setFurthestStep('setup');
  }

  function changeProfile(profileId: string): void {
    if (!entry) return;
    const next = selectableProfiles(entry).find((p) => p.id === profileId);
    if (!next) return;
    const previous = profile;
    setProfile(next);
    setForm((prev) => ({
      ...prev,
      name:
        !nameEdited || (previous && prev.name === suggestedCatalogSlug(entry, previous))
          ? machineNameFor(prev.displayName, entry, next)
          : prev.name,
      authMode: next.auth_mode,
      tokenUrl: next.token_url ?? '',
      authorizeUrl: next.authorize_url ?? '',
      scopes: (next.scopes ?? []).join(' '),
      authConfigParams: {},
    }));
  }

  const set =
    (key: keyof RegisterForm) =>
    (value: string): void =>
      setForm((prev) => ({ ...prev, [key]: value }));

  const setDisplayName = (value: string): void =>
    setForm((prev) => ({
      ...prev,
      displayName: value,
      name: isEdit || nameEdited ? prev.name : machineNameFor(value, entry, profile),
    }));

  const setCredentialField = (
    key: 'slug' | 'scope' | 'secret' | 'generated',
    value: string | boolean,
  ): void => setCredential((prev) => ({ ...prev, [key]: value }));

  const setCredentialApproval = (value: boolean): void =>
    setCredential((prev) => ({ ...prev, approvalAuto: value }));

  // The credential defaults follow the integration's names: azure → azure-credential, "Azure Access".
  const defaultCredentialSlug = defaultCredentialSlugFor(form.name);
  const credentialSlug = credential.slug.trim() || defaultCredentialSlug;
  const defaultCredentialScope = `${form.displayName.trim() || entry?.display_name || 'Integration'} Access`;
  const credentialScope = credential.scope.trim() || defaultCredentialScope;

  const takenEnvVarNames = React.useMemo(() => new Set(injectedEnvVarNames), [injectedEnvVarNames]);

  // Edit mode: the secret-backed credentials whose value can be replaced in
  // place. Secretless rows (workload identity, system) have nothing to replace.
  const replaceableCredentials = isEdit
    ? integration.credentials.filter((c) => c.vault_key !== null)
    : [];
  const draftFor = (slug: string): SecretDraft => secretDrafts[slug] ?? EMPTY_SECRET_DRAFT;
  const enteredReplacements = replaceableCredentials.filter(
    (c) => !isSecretDraftEmpty(draftFor(c.slug)),
  );
  const editClientIdLabel = clientIdLabelFor(
    integration?.catalog_key,
    integration?.auth_mode,
    integration?.auth_config?.['mechanism'],
  );
  const editClientIdPlaceholder = clientIdPlaceholderFor(
    integration?.catalog_key,
    integration?.auth_mode,
    integration?.auth_config?.['mechanism'],
  );
  const editSecretShape = secretShapeFor(
    integration?.auth_mode,
    integration?.token_endpoint_auth_method,
  );
  const editSecretNoun = secretNounFor(editSecretShape, integration?.auth_mode ?? '');

  const authConfigParamsEdited = editAuthConfigFields.some(
    (field) => form.authConfigParams[field.key] !== original.authConfigParams[field.key],
  );

  const editUrlsEdited =
    form.baseUrl !== original.baseUrl ||
    form.tokenUrl !== original.tokenUrl ||
    form.authorizeUrl !== original.authorizeUrl;

  const unchanged =
    isEdit &&
    enteredReplacements.length === 0 &&
    form.displayName === original.displayName &&
    !editUrlsEdited &&
    form.deliveryMode === original.deliveryMode &&
    form.envVarName === original.envVarName &&
    form.host === original.host &&
    form.port === original.port &&
    form.database === original.database &&
    form.scopes === original.scopes &&
    !authConfigParamsEdited &&
    JSON.stringify(tls) === JSON.stringify(tlsFormStateFrom(integration?.tls_config ?? null));

  // Certificate trust applies to an https:// base URL on a static-key integration
  // (docs/09 §Generic REST integration).
  const allowsTlsConfig =
    allowsPlainHttp && isHttpsUrl((isEdit ? form.baseUrl : resolvedBaseUrl).trim());
  const tlsConfig = allowsTlsConfig ? tlsConfigFrom(tls) : null;
  // Round-tripped through the form so a stored PEM's trailing newline is not an edit.
  const originalTlsConfig = tlsConfigFrom(tlsFormStateFrom(integration?.tls_config ?? null));
  const tlsEdited = isEdit && JSON.stringify(tlsConfig) !== JSON.stringify(originalTlsConfig);

  // The addresses the integration dials (docs/09 §Upstream Reachability), or null
  // until the form names valid ones.
  const reachabilityTargets: ReachabilityTargets | null = (() => {
    if (isSystem) return null;
    if (isDatabase) {
      const host = form.host.trim();
      const port = Number(form.port);
      return host && Number.isInteger(port) && port > 0 && port <= 65535
        ? { connection: { host, port } }
        : null;
    }
    const baseUrl = (isEdit ? form.baseUrl : resolvedBaseUrl).trim();
    const tokenUrl = isEdit
      ? form.tokenUrl !== original.tokenUrl
        ? form.tokenUrl.trim()
        : ''
      : isClientCreds || isUserDelegated
        ? resolvedTokenUrl.trim()
        : '';
    if (!isBaseUrlScheme(baseUrl) || (tokenUrl && !isHttpUrl(tokenUrl))) return null;
    return {
      baseUrl,
      ...(tokenUrl ? { tokenUrl } : {}),
      ...(tlsConfig ? { tlsConfig } : {}),
    };
  })();
  const addressEdited =
    isEdit &&
    (form.baseUrl.trim() !== original.baseUrl.trim() ||
      form.tokenUrl.trim() !== original.tokenUrl.trim() ||
      form.host.trim() !== original.host.trim() ||
      form.port !== original.port);
  const needsReachability = !isSystem && (!isEdit || addressEdited || tlsEdited);
  const currentReachability =
    reachability && reachability.key === reachabilityKey(reachabilityTargets)
      ? reachability.report
      : null;
  const insecureHttp =
    allowsPlainHttp &&
    isPlainHttpUrl(isEdit ? form.baseUrl : resolvedBaseUrl) &&
    (!isEdit || form.baseUrl !== original.baseUrl)
      ? {
          acknowledged: insecureHttpAcknowledged,
          onAcknowledgedChange: setInsecureHttpAcknowledged,
          error: errors.insecureHttp,
          httpsSuggestion:
            currentReachability?.addresses.find((a) => a.role === 'base_url')?.https_redirect ??
            null,
          ...((isEdit ? editTemplates?.baseUrl : baseUrlTemplated)
            ? {}
            : { onUseHttps: (url: string) => set('baseUrl')(url) }),
        }
      : undefined;
  const certificate = allowsTlsConfig
    ? {
        value: tls,
        onChange: (next: TlsFormState) => {
          setTls(next);
          setErrors((prev) => ({ ...prev, tls: undefined }));
        },
        error: errors.tls,
      }
    : undefined;

  /** Checks the addresses as they stand; true only when every one is reachable. */
  async function runReachabilityCheck(): Promise<boolean> {
    const key = reachabilityKey(reachabilityTargets);
    if (!reachabilityTargets || !key) {
      toast.error('Enter a valid address before checking reachability.');
      return false;
    }
    try {
      const res = await reachabilityCheck.mutateAsync({ body: reachabilityTargets });
      if (res.status !== 200) throw res;
      setReachability({ key, report: res.body });
      return res.body.reachable;
    } catch (err) {
      toast.error(extractApiError(err)?.message ?? 'The reachability check failed to run.');
      return false;
    }
  }

  function validateEdit(): boolean {
    const next: FormErrors = {};
    if (!form.displayName.trim()) next.displayName = 'Required';
    if (isDatabase) {
      if (!form.host.trim()) next.host = 'Required';
      const portNum = Number(form.port);
      if (!form.port.trim()) next.port = 'Required';
      else if (!Number.isInteger(portNum) || portNum < 1 || portNum > 65535)
        next.port = 'Enter a port 1–65535';
      if (!form.database.trim()) next.database = 'Required';
    } else if (editTemplates && editUrlsEdited) {
      const placeholderErrors = validatePlaceholders(editTemplates.placeholders);
      if (Object.keys(placeholderErrors).length > 0) next.placeholders = placeholderErrors;
    }
    if (isHttp && !editTemplates?.baseUrl && form.baseUrl !== original.baseUrl) {
      if (!form.baseUrl.trim()) next.baseUrl = 'Required';
      else if (hasUnresolvedPlaceholder(form.baseUrl))
        next.baseUrl = 'Replace the {placeholder} with your value';
      else if (!isBaseUrlScheme(form.baseUrl)) next.baseUrl = 'Enter a valid HTTPS URL';
      else if (isPlainHttpUrl(form.baseUrl) && !insecureHttpAcknowledged)
        next.insecureHttp = 'Confirm that the traffic will not be encrypted.';
    }
    if (allowsTlsConfig) {
      const tlsError = tlsFormError(tls);
      if (tlsError) next.tls = tlsError;
    }
    if (editUrlShape?.kind === 'token_url' && form.tokenUrl !== original.tokenUrl) {
      if (!form.tokenUrl.trim()) next.tokenUrl = 'Required';
      else if (hasUnresolvedPlaceholder(form.tokenUrl))
        next.tokenUrl = 'Replace the {placeholder} with your value';
      else if (!isHttpsUrl(form.tokenUrl)) next.tokenUrl = 'Enter a valid HTTPS URL';
    }
    if (showEnvVar) {
      const error = envVarNameError(
        form.envVarName.trim(),
        isDatabase,
        takenEnvVarNames,
        original.envVarName,
      );
      if (error) next.envVarName = error;
    }
    if (authConfigParamsEdited) {
      const fieldErrors = authConfigFieldErrorsFor(editAuthConfigFields, form.authConfigParams);
      if (Object.keys(fieldErrors).length > 0) next.authConfigParams = fieldErrors;
    }
    for (const cred of enteredReplacements) {
      const draftError = secretDraftError(draftFor(cred.slug), {
        shape: editSecretShape,
        clientIdLabel: editClientIdLabel,
        secretNoun: editSecretNoun,
        required: false,
      });
      if (draftError) {
        next.credentials = `Credential "${cred.slug}": ${draftError}`;
        break;
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function validatePlaceholders(
    placeholders: readonly CatalogUrlPlaceholderDto[] = urlPlaceholders,
  ): Record<string, string> {
    const placeholderErrors: Record<string, string> = {};
    for (const placeholder of placeholders) {
      const value = form.placeholderValues[placeholder.token] ?? '';
      if (!value.trim()) {
        placeholderErrors[placeholder.token] = 'Required';
      } else if (placeholder.format === 'digits' && !/^\d+$/.test(value.trim())) {
        placeholderErrors[placeholder.token] =
          placeholder.token === 'installation_id'
            ? 'Enter the numeric installation ID from the installation URL'
            : 'Enter a numeric value';
      } else if (placeholder.format === 'uuid' && !UUID_RE.test(value.trim())) {
        placeholderErrors[placeholder.token] =
          placeholder.token === 'tenant_id'
            ? 'Enter the directory (tenant) ID — a GUID, not the domain name'
            : 'Enter a GUID';
      } else if (
        placeholder.format === 'salesforce_my_domain' &&
        !isSalesforceMyDomain(normalizePlaceholderValue(value, placeholder.normalize))
      ) {
        placeholderErrors[placeholder.token] =
          'Enter your My Domain name, such as acme or acme--uat.sandbox — not login.salesforce.com or a page URL';
      }
    }
    return placeholderErrors;
  }

  /** Every registration error for the form as it stands, without showing any. */
  function registerErrors(): FormErrors {
    const next: FormErrors = {};
    if (!form.displayName.trim()) next.displayName = 'Required';
    if (!form.name.trim()) next.name = 'Required';

    if (isDatabase) {
      if (!form.host.trim()) next.host = 'Required';
      const portNum = Number(form.port);
      if (!form.port.trim()) next.port = 'Required';
      else if (!Number.isInteger(portNum) || portNum < 1 || portNum > 65535)
        next.port = 'Enter a port 1–65535';
      if (!form.database.trim()) next.database = 'Required';
      if (authMode === 'sql_password' && !form.username.trim()) next.username = 'Required';
      if (authMode === 'service_principal') {
        if (!form.clientId.trim()) next.clientId = 'Required';
        else if (!UUID_RE.test(form.clientId.trim()))
          next.clientId = 'Enter the application (client) ID — a UUID';
        if (!form.tenantId.trim()) next.tenantId = 'Required';
        else if (!UUID_RE.test(form.tenantId.trim()))
          next.tenantId = 'Enter the directory (tenant) ID — a UUID';
      }
      if (isUserDelegated) {
        const placeholderErrors = validatePlaceholders();
        if (Object.keys(placeholderErrors).length > 0) next.placeholders = placeholderErrors;
        if (!form.clientId.trim()) next.clientId = 'Required';
      }
    } else if (!isSystem) {
      const placeholderErrors = validatePlaceholders();
      if (Object.keys(placeholderErrors).length > 0) next.placeholders = placeholderErrors;

      if (baseUrlTemplated) {
        if (Object.keys(placeholderErrors).length === 0) {
          if (hasUnresolvedPlaceholder(resolvedBaseUrl)) {
            attachResolvedUrlError(
              next,
              form.baseUrl,
              'Replace the {placeholder} with your value',
              'baseUrl',
            );
          } else if (!isBaseUrlScheme(resolvedBaseUrl)) {
            attachResolvedUrlError(next, form.baseUrl, 'Enter a valid HTTPS URL', 'baseUrl');
          }
        }
      } else if (!form.baseUrl.trim()) {
        next.baseUrl = 'Required';
      } else if (hasUnresolvedPlaceholder(form.baseUrl)) {
        next.baseUrl = 'Replace the {placeholder} with your value';
      } else if (!isBaseUrlScheme(form.baseUrl)) {
        next.baseUrl = 'Enter a valid HTTPS URL';
      }
      if (
        next.baseUrl === undefined &&
        isPlainHttpUrl(resolvedBaseUrl) &&
        allowsPlainHttp &&
        !insecureHttpAcknowledged
      ) {
        next.insecureHttp = 'Confirm that the traffic will not be encrypted.';
      }
      if (allowsTlsConfig) {
        const tlsError = tlsFormError(tls);
        if (tlsError) next.tls = tlsError;
      }
      if (!authMode) next.authMode = 'Required';
      if (isUserDelegated && !form.clientId.trim()) next.clientId = 'Required';
      if (credentialRole) {
        const role = normalizeCredentialRole(form.credentialRole);
        if (!form.credentialRole.trim()) next.credentialRole = 'Required';
        else if (role === null)
          next.credentialRole =
            'Letters, digits, _ and $ only (a role created with quotes is not supported)';
        else if (credentialRole.blocked.includes(role))
          next.credentialRole = `${role} is never reachable through OAuth`;
      }
      if (isUserDelegated && !isCatalog) {
        if (!form.authorizeUrl.trim()) next.authorizeUrl = 'Required';
        else if (!isHttpsUrl(form.authorizeUrl)) next.authorizeUrl = 'Enter a valid HTTPS URL';
        if (!form.tokenUrl.trim()) next.tokenUrl = 'Required';
        else if (!isHttpsUrl(form.tokenUrl)) next.tokenUrl = 'Enter a valid HTTPS URL';
        if (parseScopes(form.scopes).length === 0) {
          next.scopes = 'Required — the scopes each user authorizes when they connect';
        }
        if (form.revokeUrl.trim() && !isHttpsUrl(form.revokeUrl)) {
          next.revokeUrl = 'Enter a valid HTTPS URL';
        }
      }
      if (isClientCreds) {
        if (!form.clientId.trim()) next.clientId = 'Required';
        else if (isGithubAppProfile && !/^\d+$/.test(form.clientId.trim()))
          next.clientId = 'Enter the numeric App ID from the GitHub App settings page';
        if (isPrivateKeyJwtProfile && !form.certificateId.trim()) next.certificateId = 'Required';
        if (isGithubAppProfile && !githubInstallationFromPlaceholder) {
          if (!form.certificateId.trim()) next.certificateId = 'Required';
          else if (!/^\d+$/.test(form.certificateId.trim()))
            next.certificateId = 'Enter the numeric installation ID from the installation URL';
        }
        if (tokenUrlTemplated) {
          if (Object.keys(placeholderErrors).length === 0) {
            if (!resolvedTokenUrl.trim()) {
              attachResolvedUrlError(
                next,
                form.tokenUrl,
                'Required for client credentials',
                'tokenUrl',
              );
            } else if (hasUnresolvedPlaceholder(resolvedTokenUrl)) {
              attachResolvedUrlError(
                next,
                form.tokenUrl,
                'Replace the {placeholder} with your value',
                'tokenUrl',
              );
            } else if (!isHttpUrl(resolvedTokenUrl)) {
              attachResolvedUrlError(
                next,
                form.tokenUrl,
                'Enter a valid URL (https://…)',
                'tokenUrl',
              );
            }
          }
        } else if (!form.tokenUrl.trim()) {
          next.tokenUrl = 'Required for client credentials';
        } else if (hasUnresolvedPlaceholder(form.tokenUrl)) {
          next.tokenUrl = 'Replace the {placeholder} with your value';
        } else if (!isHttpUrl(form.tokenUrl)) {
          next.tokenUrl = 'Enter a valid URL (https://…)';
        }
      }
      if (isClientCreds && isCatalog) {
        const fieldErrors = authConfigFieldErrors(profile ?? null, form.authConfigParams);
        if (Object.keys(fieldErrors).length > 0) next.authConfigParams = fieldErrors;
      }
      if (authMode === 'service_account') {
        if (needsMintScopes && parseScopes(form.scopes).length === 0) {
          next.scopes = 'Required — the proxy requests Google tokens with exactly these scopes';
        }
        const inject = profile?.auth_inject;
        const needsUsername =
          inject?.kind === 'basic'
            ? inject.username_from !== 'secret'
            : !isCatalog && form.authInjectKind === 'basic' && form.basicKeyPosition === 'password';
        if (needsUsername && !form.authUsername.trim()) next.authUsername = 'Required';
        const missingParams = Object.fromEntries(
          nonSecretAuthParamNames(profile)
            .filter((name) => !form.authConfigParams[name]?.trim())
            .map((name) => [name, 'Required']),
        );
        if (Object.keys(missingParams).length > 0) next.authConfigParams = missingParams;
        if (!isCatalog && form.authInjectKind === 'custom_header') {
          if (!form.authHeaderName.trim()) next.authPlacement = 'Header name is required';
          else if (!isValidHeaderName(form.authHeaderName.trim()))
            next.authPlacement = 'Enter a valid HTTP header name';
        }
        if (!isCatalog && form.authInjectKind === 'query_param') {
          if (!form.authQueryParamName.trim())
            next.authPlacement = 'Query parameter name is required';
          else if (!isValidQueryParamName(form.authQueryParamName.trim()))
            next.authPlacement = 'Enter a valid query parameter name';
        }
      }
    }

    if (showEnvVar) {
      const error = envVarNameError(form.envVarName.trim(), isDatabase, takenEnvVarNames);
      if (error) next.envVarName = error;
    }

    if (!secretless && !credential.secret.trim()) {
      next.credentials = `Paste the ${secretLabel.replace(/^[A-Z][a-z]/, (c) => c.toLowerCase())}.`;
    } else if (!CREDENTIAL_SLUG_RE.test(credentialSlug)) {
      next.credentials = `Credential identifier "${credentialSlug}" must be lowercase letters, digits, and hyphens.`;
    } else if (credentialSlug.length > 63) {
      next.credentials = 'Credential identifier must be at most 63 characters.';
    }

    return next;
  }

  /** Shows every registration error and opens the first step that has one. */
  function validateRegister(): boolean {
    const next = registerErrors();
    setErrors(next);
    const firstStep = wizardSteps.find((id) =>
      STEP_ERROR_KEYS[id].some((key) => next[key] !== undefined),
    );
    if (firstStep) setStep(firstStep);
    if (next.baseUrl || next.authPlacement) setShowCatalogDefaults(true);
    return Object.keys(next).length === 0;
  }

  async function goToNextStep(): Promise<void> {
    if (step === 'form') return;
    const stepKeys = STEP_ERROR_KEYS[step];
    const all = registerErrors();
    const stepErrors = Object.fromEntries(
      Object.entries(all).filter(([key]) => stepKeys.includes(key as keyof FormErrors)),
    ) as FormErrors;
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) {
      if (stepErrors.baseUrl || stepErrors.authPlacement) setShowCatalogDefaults(true);
      if (stepErrors.name) setNameEdited(true);
      return;
    }
    if (step === 'connect' && !(await reachabilityReady())) return;
    const next = wizardSteps[wizardSteps.indexOf(step) + 1];
    if (!next) return;
    setStep(next);
    if (WIZARD_ORDER.indexOf(next) > WIZARD_ORDER.indexOf(furthestStep)) setFurthestStep(next);
  }

  function goToPreviousStep(): void {
    if (step === 'form') return;
    const previous = wizardSteps[wizardSteps.indexOf(step) - 1];
    if (previous) {
      setErrors({});
      setStep(previous);
    }
  }

  async function handleSubmitEdit(): Promise<void> {
    if (!integration) return;
    const body: Record<string, unknown> = {};
    if (form.displayName !== original.displayName) body['displayName'] = form.displayName.trim();
    if (isHttp && form.baseUrl !== original.baseUrl) {
      body['baseUrl'] = form.baseUrl.trim();
      if (isPlainHttpUrl(form.baseUrl)) body['allowInsecureHttp'] = true;
    }
    if (tlsEdited) body['tlsConfig'] = tlsConfig;
    if ((isHttp || isInjectableDb) && form.deliveryMode !== original.deliveryMode) {
      body['deliveryMode'] = form.deliveryMode;
    }
    if ((isHttp || isInjectableDb) && form.envVarName !== original.envVarName) {
      body['envVarName'] = form.deliveryMode === 'injected' ? form.envVarName.trim() : undefined;
    }
    if (isDatabase && form.host !== original.host) body['host'] = form.host.trim();
    if (isDatabase && form.port !== original.port) body['port'] = Number(form.port);
    if (isDatabase && form.database !== original.database) {
      body['databaseName'] = form.database.trim();
    }
    // auth_config is replaced wholesale, not merged, so send the mode's complete
    // object rather than the one field that changed — otherwise a sibling key
    // (a client_credentials token_url, say) would be dropped by the edit.
    const scopesEdited = needsMintScopes && form.scopes !== original.scopes;
    const tokenUrlEdited = isHttp && form.tokenUrl !== original.tokenUrl;
    const authorizeUrlEdited = isHttp && form.authorizeUrl !== original.authorizeUrl;
    if (scopesEdited || authConfigParamsEdited || tokenUrlEdited || authorizeUrlEdited) {
      body['authConfig'] = {
        ...(integration.auth_config ?? {}),
        ...(scopesEdited ? { scopes: parseScopes(form.scopes) } : {}),
        ...(tokenUrlEdited ? { token_url: form.tokenUrl.trim() } : {}),
        ...(authorizeUrlEdited ? { authorize_url: form.authorizeUrl.trim() } : {}),
        ...Object.fromEntries(
          editAuthConfigFields.map((field) => [
            field.key,
            (form.authConfigParams[field.key] ?? '').trim(),
          ]),
        ),
      };
    }
    const replacements = enteredReplacements.map((c) => ({
      slug: c.slug,
      secret: composeSecretDraft(draftFor(c.slug), editSecretShape),
    }));
    if (Object.keys(body).length === 0 && replacements.length === 0) {
      onOpenChange(false);
      return;
    }

    try {
      // Metadata first, then secrets — a failed patch leaves every stored
      // secret untouched; a failed replace leaves the dialog open to retry.
      let updatedName = integration.name;
      let affected: AffectedApp[] = [];
      if (Object.keys(body).length > 0) {
        const res = await updateIntegration.mutateAsync({ params: { id: integration.id }, body });
        if (res.status >= 300) throw res;
        updatedName = res.body.integration.name;
        affected = res.body.affected_apps;
        if (TEST_TARGET_FIELDS.some((field) => field in body)) {
          onTestTargetChanged?.(integration.id);
        }
      }
      // Sequential, not Promise.all: each credential's rotation is an
      // independent vault write, so a failure must report exactly which
      // secrets already changed. A succeeded credential's draft is cleared, so
      // a retry re-sends only the ones that failed.
      for (const r of replacements) {
        let ok = false;
        let failure: unknown;
        try {
          const res = await replaceSecret.mutateAsync({
            params: { id: integration.id, slug: r.slug },
            body: { secret: r.secret },
          });
          ok = res.status < 300;
          failure = res;
        } catch (err) {
          failure = err;
        }
        if (!ok) {
          const replaced = replacements.slice(0, replacements.indexOf(r)).map((done) => done.slug);
          setErrors((prev) => ({
            ...prev,
            credentials:
              replaced.length > 0
                ? `Replacing "${r.slug}" failed. Already replaced: ${replaced.join(', ')} — saving again re-sends only the rest.`
                : `Replacing "${r.slug}" failed. No secrets were changed.`,
          }));
          await queryClient.invalidateQueries({ queryKey: ['integrations'] });
          throw failure;
        }
        setSecretDrafts((prev) => ({ ...prev, [r.slug]: EMPTY_SECRET_DRAFT }));
        onSecretReplaced?.(integration.id, r.slug);
      }
      toast.success(
        replacements.length > 0
          ? `Integration "${updatedName}" updated; ${replacements.length} credential ${
              replacements.length === 1 ? 'secret' : 'secrets'
            } replaced.`
          : `Integration "${updatedName}" updated.`,
      );
      if (affected.length > 0) {
        setAffectedApps(affected);
      }
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      if (affected.length === 0) onOpenChange(false);
    } catch (err) {
      const apiError = extractApiError(err);
      if (apiError) {
        if (
          apiError.code === 'integration.env_var_name_reserved' ||
          apiError.code === 'integration.env_var_name_in_use'
        ) {
          setErrors((prev) => ({ ...prev, envVarName: apiError.message }));
          return;
        }
        toast.error(apiError.message);
        return;
      }
      toast.error('Failed to update the integration. Please try again.');
    }
  }

  /** The vault secret registration stores for the first credential, composed per profile. */
  function composeRegisterSecret(): string {
    return isClientCreds
      ? isGithubAppProfile
        ? composeGithubAppSecret(
            form.clientId,
            githubInstallationFromPlaceholder
              ? (form.placeholderValues.installation_id ?? '')
              : form.certificateId,
            credential.secret,
          )
        : isPrivateKeyJwtProfile
          ? composePrivateKeyJwtSecret(form.clientId, form.certificateId, credential.secret)
          : composeClientCredentialsSecret(form.clientId, credential.secret)
      : credential.secret;
  }

  /** The non-secret `auth_config` registration sends for the chosen profile or Custom mode. */
  function registerAuthConfig(): AuthConfig | undefined {
    const mode = authMode as IntegrationAuthMode;
    const scopes = parseScopes(form.scopes);
    // A user_delegated connected DB carries its profile's advertised auth_config; a
    // sql_password one carries only the DB username; a service_principal carries the
    // Entra app's client_id + tenant_id (the password / client secret is the vaulted
    // credential); workload identity carries nothing.
    return isDatabase
      ? mode === 'user_delegated' && profile
        ? buildAuthConfig(profile, {
            tokenUrl: resolvedTokenUrl.trim(),
            authorizeUrl: resolvedAuthorizeUrl.trim(),
            scopes,
            username: '',
            nonSecretParams: {},
          })
        : mode === 'sql_password'
          ? { username: form.username.trim() }
          : mode === 'service_principal'
            ? { client_id: form.clientId.trim(), tenant_id: form.tenantId.trim() }
            : undefined
      : profile
        ? buildAuthConfig(profile, {
            tokenUrl: resolvedTokenUrl.trim(),
            authorizeUrl: resolvedAuthorizeUrl.trim(),
            scopes,
            username: form.authUsername.trim(),
            nonSecretParams: form.authConfigParams,
          })
        : mode === 'user_delegated'
          ? {
              mechanism: 'authorization_code',
              authorize_url: form.authorizeUrl.trim(),
              token_url: form.tokenUrl.trim(),
              scopes,
              ...(form.revokeUrl.trim() ? { revoke_url: form.revokeUrl.trim() } : {}),
            }
          : mode === 'client_credentials'
            ? { token_url: resolvedTokenUrl.trim(), ...(scopes.length > 0 ? { scopes } : {}) }
            : mode === 'service_account'
              ? buildCustomServiceAccountConfig(form)
              : undefined;
  }

  async function handleSubmitRegister(): Promise<void> {
    // Client Credentials (Tier 2): compose the vault JSON at submit (docs/09 §Tier 2).
    const cleanCredentials = [
      {
        slug: credentialSlug,
        scope: credentialScope,
        ...(secretless
          ? {}
          : {
              secret: composeRegisterSecret(),
            }),
        approvalMode: credential.approvalAuto ? ('auto' as const) : ('manual' as const),
        ...(isUserDelegated
          ? {
              authConfig: {
                client_id: form.clientId.trim(),
                ...(credentialRole
                  ? { role: normalizeCredentialRole(form.credentialRole) ?? '' }
                  : {}),
              },
            }
          : {}),
      },
    ];
    // Optional first Knowledge entry (T-8794) — omitted entirely (not sent as
    // an empty string) when blank, so registration seeds no Knowledge unless
    // IT actually writes something (docs/32 AC-KB-02 stays the default).
    const knowledgeBodyMd = form.knowledgeBodyMd.trim();
    const common = {
      name: form.name.trim(),
      displayName: form.displayName.trim(),
      credentials: cleanCredentials,
      ...(knowledgeBodyMd ? { knowledgeBodyMd } : {}),
      ...(needsTermsAcceptance ? { acceptDataAccessTerms: true as const } : {}),
    };

    const mode = authMode as IntegrationAuthMode;
    const authConfig = registerAuthConfig();
    // A connected database carries its connection target and is always proxied — the
    // data proxy runs its queries server-side (ADR-0030). HTTP carries baseUrl +
    // authMode + the proxied/injected choice. Both record catalog_key when registered
    // from the catalog.
    // The DTO widens engine to string | null; narrow it back to the registrable
    // enum (the seed is typed, so this only fails on a stale client bundle).
    const engine = CONNECTED_DB_ENGINES.find((e) => e === entry?.engine);
    if (isDatabase && engine === undefined) {
      toast.error('This catalog entry’s engine is not supported by this client. Reload the page.');
      return;
    }
    const body =
      isDatabase && engine !== undefined
        ? {
            ...common,
            catalogKey: entry?.catalog_key,
            authCategory: 'connected-database' as const,
            authMode: mode,
            ...(authConfig ? { authConfig } : {}),
            deliveryMode,
            ...(deliveryMode === 'injected' ? { envVarName: form.envVarName.trim() } : {}),
            connection: {
              engine,
              host: form.host.trim(),
              port: Number(form.port),
              database: form.database.trim(),
            },
          }
        : isSystem
          ? {
              // System integration (ADR-0036): the control plane serves it — no
              // base URL, no auth config, always proxied.
              ...common,
              ...(entry ? { catalogKey: entry.catalog_key } : {}),
              authMode: mode,
              deliveryMode: 'proxied' as const,
            }
          : {
              ...common,
              ...(entry ? { catalogKey: entry.catalog_key } : {}),
              baseUrl: resolvedBaseUrl.trim(),
              ...(isPlainHttpUrl(resolvedBaseUrl) ? { allowInsecureHttp: true as const } : {}),
              ...(tlsConfig ? { tlsConfig } : {}),
              authMode: mode,
              ...(authConfig ? { authConfig } : {}),
              deliveryMode: isUserDelegated || actsAsSignedInUser ? 'proxied' : form.deliveryMode,
              envVarName:
                form.deliveryMode === 'injected' && !actsAsSignedInUser
                  ? form.envVarName.trim()
                  : undefined,
            };

    try {
      const res = await create.mutateAsync({ body });
      if (res.status >= 300) throw res;
      toast.success('Integration registered.');
      if (needsTermsAcceptance) onDataAccessTermsAccepted?.();
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      if (res.status === 201 && isPerUserOauthIntegration(mode, authConfig)) {
        const authorizeUrl = (authConfig as Record<string, unknown>)['authorize_url'];
        setSignInTests([
          {
            integrationId: res.body.id,
            credentialSlug,
            name: res.body.displayName,
            catalogKey: res.body.catalogKey,
            authorizeUrl: typeof authorizeUrl === 'string' ? authorizeUrl : null,
          },
        ]);
        return;
      }
      onOpenChange(false);
    } catch (err) {
      const apiError = extractApiError(err);
      if (apiError) {
        const { code, message } = apiError;
        // Each refusal returns to the step that owns the refused field.
        if (
          code === 'integration.env_var_name_reserved' ||
          code === 'integration.env_var_name_in_use'
        ) {
          setErrors((prev) => ({ ...prev, envVarName: message }));
          if (wizardSteps.includes('delivery')) setStep('delivery');
          return;
        }
        if (code === 'integration.name_in_use') {
          setErrors((prev) => ({ ...prev, name: message }));
          setNameEdited(true);
          setStep('name');
          return;
        }
        toast.error(message);
        return;
      }
      toast.error('Failed to register integration. Please try again.');
    }
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (isEdit) {
      if (!validateEdit()) return;
      if (!(await reachabilityReady())) return;
      await handleSubmitEdit();
    } else if (step !== 'review') {
      await goToNextStep();
    } else {
      if (!validateRegister()) return;
      if (!(await reachabilityReady())) {
        // The result renders only on Connect.
        if (wizardSteps.includes('connect')) setStep('connect');
        return;
      }
      await handleSubmitRegister();
    }
  }

  async function reachabilityReady(): Promise<boolean> {
    if (!needsReachability || currentReachability?.reachable) return true;
    const submitted = submitInputs;
    const ok = await runReachabilityCheck();
    // Submit closes over the inputs as they were clicked; an edit made during the check would be lost.
    const latest = latestSubmitInputs.current;
    const edited = (Object.keys(submitted) as (keyof typeof submitted)[]).some(
      (k) => latest[k] !== submitted[k],
    );
    if (ok && edited) {
      toast.info('The form changed during the reachability check. Review it and submit again.');
      return false;
    }
    return ok;
  }

  const profileChoices = isCatalog ? selectableProfiles(entry) : [];
  const wizardSteps: WizardStep[] = [
    'pick',
    'setup',
    'name',
    ...(isSystem ? [] : (['connect'] as const)),
    'credential',
    // Delivery shows when there is a choice to make or an env var name to collect.
    ...((deliveryModes.length > 1 && !isUserDelegated && !actsAsSignedInUser) || showEnvVar
      ? (['delivery'] as const)
      : []),
    'review',
  ];
  // A profile change can drop the furthest step IT reached; the last step it
  // still offers at or before that point stays reachable.
  const reachedStep =
    [...wizardSteps]
      .reverse()
      .find((id) => WIZARD_ORDER.indexOf(id) <= WIZARD_ORDER.indexOf(furthestStep)) ?? 'pick';
  const isPending =
    reachabilityCheck.isPending ||
    (isEdit ? updateIntegration.isPending || replaceSecret.isPending : create.isPending);

  const credentialPlacementFields = (
    showPlacement = true,
    showCustomerFields = true,
  ): React.JSX.Element => (
    <CredentialPlacementFields
      isCatalog={isCatalog}
      isClientCreds={isClientCreds}
      profile={profile}
      authInjectKind={form.authInjectKind}
      basicKeyPosition={form.basicKeyPosition}
      headerName={form.authHeaderName}
      queryParamName={form.authQueryParamName}
      username={form.authUsername}
      nonSecretParams={form.authConfigParams}
      error={errors.authPlacement}
      usernameError={errors.authUsername}
      nonSecretParamErrors={errors.authConfigParams}
      onAuthInjectKindChange={(value) => set('authInjectKind')(value)}
      onBasicKeyPositionChange={(value) => set('basicKeyPosition')(value)}
      onHeaderNameChange={set('authHeaderName')}
      onQueryParamNameChange={set('authQueryParamName')}
      onUsernameChange={set('authUsername')}
      onNonSecretParamChange={(name, value) =>
        setForm((previous) => ({
          ...previous,
          authConfigParams: { ...previous.authConfigParams, [name]: value },
        }))
      }
      showPlacement={showPlacement}
      showCustomerFields={showCustomerFields}
    />
  );

  const identityFields = (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="reg-displayName">Display name</Label>
        <Input
          id="reg-displayName"
          value={form.displayName}
          onChange={(e) => set('displayName')(e.target.value)}
        />
        {errors.displayName && (
          <p className="text-[11.5px] text-destructive">{errors.displayName}</p>
        )}
      </div>
      <div className="space-y-1.5">
        <Label className="block">Machine name</Label>
        <MonoValue>{form.name}</MonoValue>
        <p className="text-[11.5px] leading-snug text-muted-foreground">
          Fixed — live app manifests reference this name. To correct it, register a new integration.
        </p>
      </div>
    </>
  );

  const deliveryFields = isInjectableDb ? (
    <>
      <DeliveryModeSelector
        deliveryModes={deliveryModes}
        selected={deliveryMode}
        onChange={set('deliveryMode')}
        proxiedDescription="App sends parameterized SQL to the proxy, which runs it and audits every query."
        injectedDescription="The connection fields and client secret are injected as env vars; the app connects with its own driver, and queries are not audited."
      />
      {isEdit && (
        <p className="text-[11.5px] leading-snug text-muted-foreground">
          Changing this never blocks the save, but running apps pick it up only on their next deploy
          or restart.
        </p>
      )}
    </>
  ) : isDatabase ? (
    <p className="rounded-gl border border-border bg-muted/30 p-3 text-[11.5px] leading-relaxed text-muted-foreground">
      Delivered <span className="font-medium text-foreground">proxied</span> — apps send
      parameterized SQL to the Greenlight data proxy, which runs each statement server-side and
      returns rows. The credential never reaches the app, and every query is audited.
    </p>
  ) : isSystem ? (
    <p className="rounded-gl border border-border bg-muted/30 p-3 text-[11.5px] leading-relaxed text-muted-foreground">
      Delivered <span className="font-medium text-foreground">proxied</span> — apps call the
      Greenlight data proxy with their existing data key, and Greenlight serves the data itself.
      Access still requires a granted credential, and every call is audited.
    </p>
  ) : (
    <>
      <DeliveryModeSelector
        deliveryModes={deliveryModes}
        selected={form.deliveryMode}
        onChange={set('deliveryMode')}
        proxiedUnavailableNote={isCatalog && !deliveryModes.includes('proxied')}
        {...(isGithubAppProfile
          ? {
              injectedUnavailableNote:
                'GitHub App installation tokens last one hour and are minted per call, so this integration is proxied — the App private key never enters an app pod.',
            }
          : isUserDelegated
            ? {
                injectedUnavailableNote:
                  'Each call runs with the signed-in user’s own token, so this integration is proxied — there is no single value to place in an env var.',
              }
            : {})}
      />
      {isEdit && (
        <p className="text-[11.5px] leading-snug text-muted-foreground">
          Changing this never blocks the save, but running apps pick it up only on their next deploy
          or restart.
        </p>
      )}
    </>
  );

  if (!isEdit && signInTests !== null) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Test sign-in</DialogTitle>
            <DialogDescription>
              Registered. Before anyone uses it, sign in as yourself. It runs the same connect a
              person using an app runs, so it fails where theirs would, and a pass leaves you
              connected.
            </DialogDescription>
          </DialogHeader>
          <SignInTestPanel
            targets={signInTests}
            onSettled={() => void queryClient.invalidateQueries({ queryKey: ['integrations'] })}
          />
          <DialogFooter>
            <Button type="button" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (!isEdit && fabricPieces !== null) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
          <FabricRegistrationForm
            entries={catalogEntries}
            initialPieces={fabricPieces}
            requireDataAccessTerms={needsTermsAcceptance}
            onDataAccessTermsAccepted={onDataAccessTermsAccepted}
            onBack={() => {
              setFabricPieces(null);
              setStep('pick');
            }}
            onServicePrincipal={(catalogKey) => {
              const picked = catalogEntries.find((e) => e.catalog_key === catalogKey);
              const profile = picked && defaultSelectableProfile(picked);
              if (picked && profile) pickEntry(picked, profile.id);
            }}
            onRegistered={() => onOpenChange(false)}
          />
        </DialogContent>
      </Dialog>
    );
  }

  if (!isEdit) {
    const currentStep: WizardStep = step === 'form' ? 'pick' : step;
    // Picking the integration is the way in, not a step: the stepper starts at Set up.
    const shownSteps: WizardStep[] = wizardSteps.filter((id) => id !== 'pick');
    const stepIndex = shownSteps.indexOf(currentStep);
    const stepConfigs: StepConfig[] = shownSteps.map((id) => ({
      id,
      label: STEP_LABELS[id],
      complete: wizardSteps.indexOf(id) < wizardSteps.indexOf(reachedStep) && id !== currentStep,
    }));
    const serviceName = isCatalog ? entry.display_name : form.displayName.trim() || 'the service';
    const stepDescription: Record<WizardStep, string> = {
      pick: 'Pick an integration from the catalog, or configure a Custom one.',
      setup: isCatalog
        ? `Get a credential ready in ${entry.display_name} before you continue.`
        : 'Choose how the service checks who is calling it.',
      name: 'Name this integration the way builders will see it.',
      connect: `Tell Greenlight where ${serviceName} lives. Greenlight checks it can reach it before you continue.`,
      credential: `Paste the credential Greenlight uses to call ${serviceName}. It is stored as a secret and never shown again.`,
      delivery: 'Choose how apps receive this integration.',
      review: 'Check the details, then register.',
    };
    const resolvedAddress = isDatabase
      ? `${form.host.trim()}:${form.port}${form.database.trim() ? ` / ${form.database.trim()}` : ''}`
      : resolvedBaseUrl.trim();
    const authLabel =
      profile?.display_name ??
      (authMode ? AUTH_MODE_DISPLAY_NAMES[authMode as IntegrationAuthMode] : '');

    const summaryRow = (label: string, value: React.ReactNode, editStep?: WizardStep) => (
      <div className="flex items-start gap-3 px-3 py-2.5">
        <dt className="w-28 shrink-0 text-[12px] text-muted-foreground">{label}</dt>
        <dd className="min-w-0 flex-1 break-words text-[13px] text-foreground">{value}</dd>
        {editStep && wizardSteps.includes(editStep) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-[12px] text-muted-foreground"
            onClick={() => setStep(editStep)}
          >
            Edit
          </Button>
        )}
      </div>
    );

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3">
              {isCatalog && currentStep !== 'pick' && (
                <IntegrationTile
                  slug={entry.catalog_key}
                  catalogKey={entry.catalog_key}
                  displayName={entry.display_name}
                  size={32}
                />
              )}
              <div className="min-w-0">
                <DialogTitle>
                  {currentStep === 'pick'
                    ? 'Register integration'
                    : isCatalog
                      ? `Register ${entry.display_name}`
                      : 'Register custom integration'}
                </DialogTitle>
                <DialogDescription>{stepDescription[currentStep]}</DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {currentStep === 'pick' ? (
            <CatalogPicker
              entries={catalogEntries}
              isLoading={catalogQuery.isLoading}
              isError={catalogError}
              onSelect={pickEntry}
              onCustom={pickCustom}
            />
          ) : (
            <Stepper
              steps={stepConfigs}
              currentStepId={currentStep}
              onNavigate={(id) => {
                setErrors({});
                setStep(id as WizardStep);
              }}
              label="Registration steps"
              testId="register-stepper"
              compact
            >
              <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
                {/* The setup guide stays in reach on every later step, collapsed. */}
                {currentStep !== 'setup' && isCatalog && profile?.setup_guide?.steps?.length ? (
                  <AcquisitionGuidePanel
                    credentialKind={profile.credential_kind}
                    instructions={profile.acquisition_instructions}
                    docsUrl={profile.acquisition_docs_url}
                    docsLabel={profile.setup_guide?.docs_label}
                    steps={profile.setup_guide?.steps}
                    stepsTitle={profile.setup_guide?.steps_title ?? 'Setup guide'}
                    {...(setupStatement ? { statement: setupStatement } : {})}
                  />
                ) : null}
                {currentStep === 'setup' &&
                  (isCatalog ? (
                    <>
                      {profileChoices.length > 1 && (
                        <div className="space-y-1.5">
                          <Label htmlFor="reg-profile-mode">Connection profile</Label>
                          <Select value={profile?.id ?? ''} onValueChange={changeProfile}>
                            <SelectTrigger id="reg-profile-mode">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {profileChoices.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.display_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {profile && (
                        <AcquisitionGuidePanel
                          credentialKind={profile.credential_kind}
                          instructions={profile.acquisition_instructions}
                          docsUrl={profile.acquisition_docs_url}
                          docsLabel={profile.setup_guide?.docs_label}
                          steps={profile.setup_guide?.steps}
                          stepsTitle={profile.setup_guide?.steps_title ?? 'Setup guide'}
                          defaultOpen
                          {...(setupStatement ? { statement: setupStatement } : {})}
                        />
                      )}
                      {setupWalkthrough && (
                        <section
                          aria-labelledby="setup-walkthrough-title"
                          className="space-y-2 rounded-gl border border-border p-3"
                          data-testid="setup-walkthrough"
                        >
                          <h3
                            id="setup-walkthrough-title"
                            className="text-[12px] font-medium text-foreground"
                          >
                            {setupWalkthrough.title}
                          </h3>
                          <Walkthrough
                            walkthrough={setupWalkthrough}
                            values={{ callbackUrl: connectCallbackUrl }}
                          />
                        </section>
                      )}
                    </>
                  ) : (
                    <fieldset className="space-y-2">
                      <legend className="text-sm font-medium text-foreground">
                        How does the service authenticate?
                      </legend>
                      <div className="grid grid-cols-1 gap-2">
                        {CUSTOM_AUTH_MODES.map((mode) => {
                          const selected = form.authMode === mode.value;
                          return (
                            <button
                              key={mode.value}
                              type="button"
                              aria-pressed={selected}
                              data-testid={`custom-auth-${mode.value}`}
                              onClick={() => set('authMode')(mode.value)}
                              className={cn(
                                'flex flex-col rounded-gl border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                selected
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:bg-accent/50',
                              )}
                            >
                              <span className="text-[13px] font-medium text-foreground">
                                {AUTH_MODE_DISPLAY_NAMES[mode.value]}
                              </span>
                              <span className="text-[11.5px] leading-snug text-muted-foreground">
                                {mode.description}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {errors.authMode && (
                        <p className="text-[11.5px] text-destructive">
                          Choose how it authenticates.
                        </p>
                      )}
                    </fieldset>
                  ))}

                {currentStep === 'name' && (
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="reg-displayName">Display name</Label>
                      <Input
                        id="reg-displayName"
                        value={form.displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder={
                          isCatalog
                            ? catalogDisplayNamePlaceholder(entry.display_name)
                            : 'e.g. HubSpot — Prod'
                        }
                        autoFocus
                      />
                      {errors.displayName && (
                        <p className="text-[11.5px] text-destructive">{errors.displayName}</p>
                      )}
                    </div>
                    {nameEdited ? (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between gap-3">
                          <Label htmlFor="reg-name">Machine name</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-[12px] text-muted-foreground"
                            onClick={() => {
                              setNameEdited(false);
                              setForm((prev) => ({
                                ...prev,
                                name: machineNameFor(prev.displayName, entry, profile),
                              }));
                            }}
                          >
                            Use automatic name
                          </Button>
                        </div>
                        <Input
                          id="reg-name"
                          value={form.name}
                          onChange={(e) => set('name')(e.target.value)}
                          placeholder={
                            isCatalog
                              ? catalogSlugPlaceholder(entry.catalog_key)
                              : 'e.g. hubspot-prod'
                          }
                          className="font-mono"
                        />
                        {errors.name && (
                          <p className="text-[11.5px] text-destructive">{errors.name}</p>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-3 rounded-gl border border-border bg-muted/30 px-3 py-2.5">
                        <div className="min-w-0">
                          <p className="text-[11.5px] text-muted-foreground">Machine name</p>
                          <p className="truncate font-mono text-[13px] text-foreground">
                            {form.name || '—'}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[12px]"
                          onClick={() => setNameEdited(true)}
                        >
                          Edit
                        </Button>
                      </div>
                    )}
                    <p className="text-[11.5px] leading-snug text-muted-foreground">
                      Apps and builder agents refer to the integration by its machine name. It can’t
                      be changed after registration.
                    </p>
                  </div>
                )}

                {currentStep === 'connect' && (
                  <>
                    {isHttp && (
                      <>
                        {isCatalog ? (
                          placeholderFields
                        ) : (
                          <BaseUrlField
                            id="reg-baseUrl"
                            value={form.baseUrl}
                            onChange={set('baseUrl')}
                            error={errors.baseUrl}
                          />
                        )}
                        {authMode && credentialPlacementFields(!isCatalog)}
                        {needsMintScopes && (
                          <div className="space-y-1.5">
                            <Label htmlFor="reg-mint-scopes">OAuth scopes (space-separated)</Label>
                            <Input
                              id="reg-mint-scopes"
                              value={form.scopes}
                              onChange={(e) => set('scopes')(e.target.value)}
                              placeholder="https://www.googleapis.com/auth/drive.readonly"
                              className="font-mono"
                            />
                            <p className="text-[11.5px] leading-snug text-muted-foreground">
                              The scopes the proxy requests when minting Google tokens — match the
                              scopes authorized for Domain-Wide Delegation.
                            </p>
                            {errors.scopes && (
                              <p className="text-[11.5px] text-destructive">{errors.scopes}</p>
                            )}
                          </div>
                        )}
                        {isUserDelegated && !isCatalog && (
                          <>
                            <div className="space-y-1.5">
                              <Label htmlFor="reg-connect-authorize-url">Authorize URL</Label>
                              <Input
                                id="reg-connect-authorize-url"
                                value={form.authorizeUrl}
                                onChange={(e) => set('authorizeUrl')(e.target.value)}
                                placeholder="https://login.microsoftonline.com/<tenant id>/oauth2/v2.0/authorize"
                              />
                              <p className="text-[11.5px] leading-snug text-muted-foreground">
                                Where each user signs in to the upstream to connect their account.
                                On Microsoft Entra, name the tenant id, never /common.
                              </p>
                              {errors.authorizeUrl && (
                                <p className="text-[11.5px] text-destructive">
                                  {errors.authorizeUrl}
                                </p>
                              )}
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="reg-connect-token-url">Token URL</Label>
                              <Input
                                id="reg-connect-token-url"
                                value={form.tokenUrl}
                                onChange={(e) => set('tokenUrl')(e.target.value)}
                                placeholder="https://login.microsoftonline.com/<tenant id>/oauth2/v2.0/token"
                              />
                              {errors.tokenUrl && (
                                <p className="text-[11.5px] text-destructive">{errors.tokenUrl}</p>
                              )}
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="reg-connect-scopes">Scopes (space-separated)</Label>
                              <Input
                                id="reg-connect-scopes"
                                value={form.scopes}
                                onChange={(e) => set('scopes')(e.target.value)}
                                placeholder="openid profile offline_access https://graph.microsoft.com/.default"
                              />
                              <p className="text-[11.5px] leading-snug text-muted-foreground">
                                What each user authorizes when they connect. Include openid,
                                profile, and offline_access on Microsoft Entra, or openid and email
                                elsewhere, so Greenlight can confirm the account and refresh its
                                token.
                              </p>
                              {errors.scopes && (
                                <p className="text-[11.5px] text-destructive">{errors.scopes}</p>
                              )}
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="reg-connect-revoke-url">
                                Revocation URL (optional)
                              </Label>
                              <Input
                                id="reg-connect-revoke-url"
                                value={form.revokeUrl}
                                onChange={(e) => set('revokeUrl')(e.target.value)}
                                placeholder="https://…/oauth2/revoke"
                              />
                              {errors.revokeUrl && (
                                <p className="text-[11.5px] text-destructive">{errors.revokeUrl}</p>
                              )}
                            </div>
                          </>
                        )}
                        {isClientCreds && (
                          <>
                            {!tokenUrlTemplated && (
                              <div className="space-y-1.5">
                                <Label htmlFor="reg-tokenUrl">OAuth token URL</Label>
                                <Input
                                  id="reg-tokenUrl"
                                  value={form.tokenUrl}
                                  onChange={(e) => set('tokenUrl')(e.target.value)}
                                  placeholder="https://login.example.com/oauth2/token"
                                />
                                {errors.tokenUrl && (
                                  <p className="text-[11.5px] text-destructive">
                                    {errors.tokenUrl}
                                  </p>
                                )}
                              </div>
                            )}
                            <div className="space-y-1.5">
                              <Label htmlFor="reg-scopes">Scopes (optional, space-separated)</Label>
                              <Input
                                id="reg-scopes"
                                value={form.scopes}
                                onChange={(e) => set('scopes')(e.target.value)}
                                placeholder="e.g. read write"
                              />
                            </div>
                          </>
                        )}
                        {isCatalog && (
                          <div className="overflow-hidden rounded-gl border border-border">
                            <button
                              type="button"
                              className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                              aria-expanded={showCatalogDefaults}
                              aria-controls="catalog-default-settings"
                              onClick={() => setShowCatalogDefaults((shown) => !shown)}
                            >
                              <span>
                                <span className="block text-[13px] font-medium text-foreground">
                                  Provider defaults
                                </span>
                                <span className="block text-[11.5px] text-muted-foreground">
                                  Set by the catalog: {authLabel}
                                </span>
                              </span>
                              <ChevronDown
                                className={cn(
                                  'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                                  showCatalogDefaults && 'rotate-180',
                                )}
                                aria-hidden="true"
                              />
                            </button>
                            {showCatalogDefaults && (
                              <div
                                id="catalog-default-settings"
                                className="space-y-4 border-t border-border p-3"
                              >
                                {!baseUrlTemplated && (
                                  <BaseUrlField
                                    id="reg-baseUrl"
                                    value={form.baseUrl}
                                    onChange={set('baseUrl')}
                                    error={errors.baseUrl}
                                  />
                                )}
                                {authMode && credentialPlacementFields(true, false)}
                              </div>
                            )}
                          </div>
                        )}
                        <RegisterConnectivityStep
                          canCheck={reachabilityTargets !== null}
                          checking={reachabilityCheck.isPending}
                          report={currentReachability}
                          onCheck={() => void runReachabilityCheck()}
                          insecureHttp={insecureHttp}
                          allowsPlainHttp={allowsPlainHttp}
                          certificate={certificate}
                        />
                      </>
                    )}
                    {isDatabase && (
                      <>
                        <RegisterConnectivityStep
                          canCheck={reachabilityTargets !== null}
                          checking={reachabilityCheck.isPending}
                          report={currentReachability}
                          onCheck={() => void runReachabilityCheck()}
                          connection={{
                            host: form.host,
                            port: form.port,
                            onHostChange: set('host'),
                            onPortChange: set('port'),
                            hostError: errors.host,
                            portError: errors.port,
                          }}
                        />
                        <DatabaseNameField
                          id="reg-database"
                          value={form.database}
                          onChange={set('database')}
                          error={errors.database}
                        />
                        {isUserDelegated && isCatalog && placeholderFields}
                      </>
                    )}
                  </>
                )}

                {currentStep === 'credential' && (
                  <CredentialFieldset
                    credential={credential}
                    isDatabase={isDatabase}
                    secretNoun={secretNoun}
                    secretMultiline={isPrivateKeyJwtProfile || isGithubAppProfile}
                    generateCertificate={isPrivateKeyJwtProfile}
                    secretless={secretless}
                    catalogMode={isCatalog}
                    secretLabel={secretLabel}
                    secretPlaceholder={profile?.secret_placeholder ?? undefined}
                    defaultSlug={defaultCredentialSlug}
                    defaultScope={defaultCredentialScope}
                    showEnvVar={showEnvVar}
                    error={errors.credentials}
                    onChange={setCredentialField}
                    onApproval={setCredentialApproval}
                    identityFields={
                      <>
                        {credentialRole && (
                          <div className="space-y-1.5">
                            <Label htmlFor="reg-credential-role">{credentialRole.label}</Label>
                            <Input
                              id="reg-credential-role"
                              value={form.credentialRole}
                              onChange={(e) => set('credentialRole')(e.target.value)}
                              placeholder={credentialRole.example}
                              className="font-mono"
                              autoComplete="off"
                            />
                            <p className="text-[11.5px] leading-snug text-muted-foreground">
                              {credentialRole.hint}
                            </p>
                            {errors.credentialRole && (
                              <p className="text-[11.5px] text-destructive">
                                {errors.credentialRole}
                              </p>
                            )}
                          </div>
                        )}
                        {isUserDelegated && upstreamClientIdField}
                        {isClientCreds && (
                          <>
                            <div className="space-y-1.5">
                              <Label htmlFor="reg-oauth-client-id">{clientIdLabel}</Label>
                              <Input
                                id="reg-oauth-client-id"
                                value={form.clientId}
                                onChange={(e) => set('clientId')(e.target.value)}
                                placeholder={clientIdPlaceholder}
                                className="font-mono"
                                autoComplete="off"
                              />
                              {errors.clientId && (
                                <p className="text-[11.5px] text-destructive">{errors.clientId}</p>
                              )}
                            </div>
                            {isGithubAppProfile && !githubInstallationFromPlaceholder && (
                              <div className="space-y-1.5">
                                <Label htmlFor="reg-github-installation-id">Installation ID</Label>
                                <Input
                                  id="reg-github-installation-id"
                                  value={form.certificateId}
                                  onChange={(e) => set('certificateId')(e.target.value)}
                                  placeholder="Numeric id at the end of the installation URL"
                                  className="font-mono"
                                  autoComplete="off"
                                />
                                {errors.certificateId && (
                                  <p className="text-[11.5px] text-destructive">
                                    {errors.certificateId}
                                  </p>
                                )}
                              </div>
                            )}
                            {isPrivateKeyJwtProfile && (
                              <div className="space-y-1.5">
                                <Label htmlFor="reg-oauth-certificate-id">Certificate ID</Label>
                                <Input
                                  id="reg-oauth-certificate-id"
                                  value={form.certificateId}
                                  onChange={(e) => set('certificateId')(e.target.value)}
                                  placeholder="Certificate ID from the M2M mapping row"
                                  className="font-mono"
                                  autoComplete="off"
                                />
                                {errors.certificateId && (
                                  <p className="text-[11.5px] text-destructive">
                                    {errors.certificateId}
                                  </p>
                                )}
                              </div>
                            )}
                          </>
                        )}
                        {isDatabase && authMode === 'sql_password' && (
                          <div className="space-y-1.5">
                            <Label htmlFor="reg-username">Database username</Label>
                            <Input
                              id="reg-username"
                              value={form.username}
                              onChange={(e) => set('username')(e.target.value)}
                              placeholder="e.g. greenlight_reader"
                              className="font-mono"
                              autoComplete="off"
                            />
                            {errors.username && (
                              <p className="text-[11.5px] text-destructive">{errors.username}</p>
                            )}
                          </div>
                        )}
                        {isDatabase && authMode === 'service_principal' && (
                          <>
                            <div className="space-y-1.5">
                              <Label htmlFor="reg-client-id">Application (client) ID</Label>
                              <Input
                                id="reg-client-id"
                                value={form.clientId}
                                onChange={(e) => set('clientId')(e.target.value)}
                                placeholder="00000000-0000-0000-0000-000000000000"
                                className="font-mono"
                                autoComplete="off"
                              />
                              {errors.clientId && (
                                <p className="text-[11.5px] text-destructive">{errors.clientId}</p>
                              )}
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="reg-tenant-id">Directory (tenant) ID</Label>
                              <Input
                                id="reg-tenant-id"
                                value={form.tenantId}
                                onChange={(e) => set('tenantId')(e.target.value)}
                                placeholder="00000000-0000-0000-0000-000000000000"
                                className="font-mono"
                                autoComplete="off"
                              />
                              <p className="text-[11.5px] text-muted-foreground">
                                The tenant the Entra app is registered in — the database’s own
                                tenant.
                              </p>
                              {errors.tenantId && (
                                <p className="text-[11.5px] text-destructive">{errors.tenantId}</p>
                              )}
                            </div>
                          </>
                        )}
                      </>
                    }
                  />
                )}

                {currentStep === 'delivery' && (
                  <>
                    {deliveryFields}
                    {showEnvVar && (
                      <EnvVarNameField
                        id="reg-envVarName"
                        value={form.envVarName}
                        onChange={set('envVarName')}
                        isDatabase={isDatabase}
                        error={errors.envVarName}
                      />
                    )}
                  </>
                )}

                {currentStep === 'review' && (
                  <>
                    <dl
                      className="divide-y divide-border rounded-gl border border-border"
                      data-testid="register-review"
                    >
                      {summaryRow(
                        'Integration',
                        <>
                          {form.displayName.trim()}{' '}
                          <span className="font-mono text-[12px] text-muted-foreground">
                            {form.name.trim()}
                          </span>
                        </>,
                        'name',
                      )}
                      {!isSystem &&
                        summaryRow(
                          'Address',
                          <span className="font-mono text-[12px]">{resolvedAddress}</span>,
                          'connect',
                        )}
                      {summaryRow('Authentication', authLabel, 'setup')}
                      {summaryRow(
                        'Credential',
                        <>
                          <span className="font-mono text-[12px]">{credentialSlug}</span> ·{' '}
                          {credentialScope} ·{' '}
                          {credential.approvalAuto ? 'grants auto-approved' : 'IT approves grants'}
                        </>,
                        'credential',
                      )}
                      {summaryRow(
                        'Delivery',
                        deliveryMode === 'injected' && showEnvVar
                          ? `Injected as ${form.envVarName.trim()}`
                          : 'Proxied',
                        'delivery',
                      )}
                      {!isSystem &&
                        summaryRow(
                          'Reachability',
                          currentReachability?.reachable
                            ? 'Greenlight can reach every address.'
                            : 'Checked again when you register.',
                        )}
                    </dl>
                    <div className="space-y-1.5">
                      <Label htmlFor="reg-knowledge">First Knowledge entry (optional)</Label>
                      <p className="text-xs text-muted-foreground">
                        What builder agents need to use this integration well: what data it reaches
                        (for example, the Sales workspace and the Revenue semantic model), schema,
                        idiomatic usage, gotchas. Not where or how to get credentials.
                      </p>
                      <Textarea
                        id="reg-knowledge"
                        value={form.knowledgeBodyMd}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, knowledgeBodyMd: e.target.value }))
                        }
                        placeholder="e.g. Sales reporting lives in the Sales workspace; take revenue figures from the Revenue semantic model, not the raw tables."
                        rows={3}
                        className="font-mono"
                        data-testid="reg-knowledge-body"
                      />
                    </div>
                    <DataAccessTermsPanel
                      providerName={
                        isCatalog ? entry.display_name : form.displayName.trim() || null
                      }
                      injected={showEnvVar}
                      userPassthrough={isUserDelegated}
                      orgWideDelegation={actsAsSignedInUser}
                      {...(needsTermsAcceptance
                        ? {
                            acceptance: {
                              accepted: termsAccepted,
                              onAcceptedChange: setTermsAccepted,
                            },
                          }
                        : {})}
                    />
                  </>
                )}

                <DialogFooter className="gap-2 sm:justify-between">
                  <Button type="button" variant="outline" onClick={goToPreviousStep}>
                    <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
                    Back
                  </Button>
                  {currentStep === 'review' ? (
                    <Button
                      type="submit"
                      disabled={isPending || (needsTermsAcceptance && !termsAccepted)}
                    >
                      {reachabilityCheck.isPending
                        ? 'Checking reachability…'
                        : isPending
                          ? 'Registering…'
                          : 'Register'}
                    </Button>
                  ) : (
                    <Button type="submit" disabled={reachabilityCheck.isPending}>
                      {reachabilityCheck.isPending ? 'Checking reachability…' : 'Continue'}
                    </Button>
                  )}
                </DialogFooter>
                <p className="sr-only" aria-live="polite">
                  Step {stepIndex + 1} of {shownSteps.length}: {STEP_LABELS[currentStep]}
                </p>
              </form>
            </Stepper>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit integration</DialogTitle>
          <DialogDescription>
            {`Correct ${integration.name}'s record without deleting and re-registering it. Stored secrets are never shown; entering a new value replaces one.`}
          </DialogDescription>
        </DialogHeader>

        {affectedApps && affectedApps.length > 0 && (
          <div
            className="rounded-gl border border-gl-amber/30 bg-gl-amber/10 px-3 py-2.5 text-[12.5px] leading-relaxed text-gl-amber-text"
            role="status"
            data-testid="edit-integration-affected-apps"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <div className="min-w-0 space-y-1">
                <p className="font-medium">
                  {affectedApps.length === 1 ? 'An app holds' : 'Apps hold'} an active grant on this
                  integration.
                </p>
                <p className="break-words">
                  {affectedApps.map((a) => a.name).join(', ')} will pick up this change on{' '}
                  {affectedApps.length === 1 ? 'its' : 'their'} next deploy or restart.
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
          {identityFields}

          {isHttp && (
            <>
              {!editTemplates?.baseUrl && (
                <BaseUrlField
                  id="reg-baseUrl"
                  value={form.baseUrl}
                  onChange={set('baseUrl')}
                  error={errors.baseUrl}
                  markPlainHttp
                />
              )}
              {editPlaceholderFields}
              {editUrlShape?.kind === 'token_url' && (
                <div className="space-y-1.5">
                  <Label htmlFor="edit-tokenUrl">OAuth token URL</Label>
                  <Input
                    id="edit-tokenUrl"
                    value={form.tokenUrl}
                    onChange={(e) => set('tokenUrl')(e.target.value)}
                    placeholder="https://login.example.com/oauth2/token"
                    className="font-mono"
                  />
                  <p className="text-[11.5px] leading-snug text-muted-foreground">
                    The OAuth token endpoint the proxy calls for access tokens.
                  </p>
                  {errors.tokenUrl && (
                    <p className="text-[11.5px] text-destructive">{errors.tokenUrl}</p>
                  )}
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Auth mode</Label>
                <p className="rounded-gl border border-border bg-muted/30 px-3 py-2 text-[13px] text-foreground">
                  {authMode}
                </p>
                <p className="text-[11.5px] leading-snug text-muted-foreground">
                  Fixed — determines the shape every credential's secret must hold.
                </p>
              </div>

              {editAuthConfigFields.length > 0 && (
                <AuthConfigFieldInputs
                  fields={editAuthConfigFields}
                  values={form.authConfigParams}
                  errors={errors.authConfigParams}
                  onChange={(key, value) =>
                    setForm((previous) => ({
                      ...previous,
                      authConfigParams: { ...previous.authConfigParams, [key]: value },
                    }))
                  }
                />
              )}

              {needsMintScopes && (
                <div className="space-y-1.5">
                  <Label htmlFor="reg-mint-scopes">OAuth scopes (space-separated)</Label>
                  <Input
                    id="reg-mint-scopes"
                    value={form.scopes}
                    onChange={(e) => set('scopes')(e.target.value)}
                    placeholder="https://www.googleapis.com/auth/drive.readonly"
                    className="font-mono"
                  />
                  <p className="text-[11.5px] leading-snug text-muted-foreground">
                    The scopes the proxy requests when minting Google tokens — match the scopes
                    authorized for Domain-Wide Delegation.
                  </p>
                  {errors.scopes && (
                    <p className="text-[11.5px] text-destructive">{errors.scopes}</p>
                  )}
                </div>
              )}

              {needsReachability && (
                <RegisterConnectivityStep
                  canCheck={reachabilityTargets !== null}
                  checking={reachabilityCheck.isPending}
                  report={currentReachability}
                  onCheck={() => void runReachabilityCheck()}
                  insecureHttp={insecureHttp}
                  allowsPlainHttp={allowsPlainHttp}
                />
              )}
              {/* One slot whether or not the check shows, so toggling the check
                  does not rebuild the section and drop its open state or focus. */}
              {certificate && (
                <CertificateTrustSection
                  {...certificate}
                  untrustedChain={
                    needsReachability &&
                    (currentReachability?.addresses.some(
                      (a) => a.role === 'base_url' && a.untrusted_chain,
                    ) ??
                      false)
                  }
                />
              )}
            </>
          )}

          {isDatabase && (
            <>
              <div className="space-y-1.5">
                <Label className="block">Engine</Label>
                <MonoValue>{integration.engine}</MonoValue>
              </div>
              <RegisterConnectivityStep
                canCheck={reachabilityTargets !== null}
                checking={reachabilityCheck.isPending}
                report={currentReachability}
                onCheck={() => void runReachabilityCheck()}
                connection={{
                  host: form.host,
                  port: form.port,
                  onHostChange: set('host'),
                  onPortChange: set('port'),
                  hostError: errors.host,
                  portError: errors.port,
                }}
              />
              <DatabaseNameField
                id="reg-database"
                value={form.database}
                onChange={set('database')}
                error={errors.database}
              />
            </>
          )}

          {deliveryFields}

          {showEnvVar && (
            <EnvVarNameField
              id="reg-envVarName"
              value={form.envVarName}
              onChange={set('envVarName')}
              isDatabase={isDatabase}
              error={errors.envVarName}
            />
          )}

          {replaceableCredentials.length > 0 && (
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-foreground">Credentials</legend>
              <p className="text-[11.5px] leading-snug text-muted-foreground">
                Stored secrets are never shown. Enter a new value to replace one; leave it blank to
                keep the current value.
              </p>
              {replaceableCredentials.map((cred) => (
                <div
                  key={cred.slug}
                  className="space-y-2 rounded-gl border border-border bg-muted/30 p-3"
                  data-testid={`edit-credential-${cred.slug}`}
                >
                  <p className="font-mono text-xs text-foreground">{cred.slug}</p>
                  <CredentialSecretInputs
                    idPrefix={`edit-cred-${cred.slug}`}
                    shape={editSecretShape}
                    clientIdLabel={editClientIdLabel}
                    clientIdPlaceholder={editClientIdPlaceholder}
                    secretNoun={editSecretNoun}
                    draft={draftFor(cred.slug)}
                    onChange={(draft) =>
                      setSecretDrafts((prev) => ({ ...prev, [cred.slug]: draft }))
                    }
                    replacement
                  />
                </div>
              ))}
              {enteredReplacements.length > 0 && integration.delivery_mode === 'injected' && (
                <p
                  className="flex items-start gap-1.5 rounded-gl border border-gl-amber/30 bg-gl-amber/10 px-3 py-2.5 text-[12.5px] leading-relaxed text-gl-amber-text"
                  role="status"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>
                    Injected delivery: running apps keep the old value until their next deploy.{' '}
                    {grantedGrantsQuery.isLoading
                      ? 'Checking which apps hold this credential…'
                      : injectedAffectedNames.length > 0
                        ? `Affected ${injectedAffectedNames.length === 1 ? 'app' : 'apps'}: ${injectedAffectedNames.join(', ')}${injectedAffectedTruncated ? ' and others' : ''}.`
                        : grantedGrantsPage
                          ? 'No apps currently hold a grant here.'
                          : 'Any granted app may keep the old value.'}
                  </span>
                </p>
              )}
              {errors.credentials && (
                <p className="text-[11.5px] text-destructive">{errors.credentials}</p>
              )}
            </fieldset>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {affectedApps ? 'Close' : 'Cancel'}
            </Button>
            {!affectedApps && (
              <Button type="submit" disabled={isPending || unchanged}>
                {reachabilityCheck.isPending
                  ? 'Checking reachability…'
                  : isPending
                    ? 'Saving…'
                    : 'Save changes'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
